import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, User, 
  CreditCard, Banknote, Smartphone, Building, X,
  Plus, Minus, CalendarDays, ArrowRightLeft, XCircle, FileText,
  Package, CheckCircle, AlertCircle, Trash2, ShieldBan, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getKioskToken, fetchKioskTransactionsData, KIOSK_API_BASE } from '../../lib/kioskTokenManager';

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

// Kiosk API endpoints are imported from shared kioskTokenManager

// Format time for display in PHP-style `h:mm am/pm` (e.g. "8:59 am")
// Accepts: "13:00:00", "13:00", "10AM", "2PM", "08:30 AM"
const formatKioskTimeDisplay = (timeStr) => {
  if (!timeStr) return '';

  const raw = String(timeStr).trim();

  // 24-hour formats: HH:MM or HH:MM:SS
  const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hour24 = parseInt(match24[1], 10);
    const minutes = match24[2];
    if (!Number.isFinite(hour24)) hour24 = 0;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  // AM/PM formats: 10AM, 2PM, 08:30 AM
  const matchAmPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (matchAmPm) {
    let hour = parseInt(matchAmPm[1], 10);
    const minutes = matchAmPm[2] || '00';
    const ampm = matchAmPm[3].toLowerCase();
    return `${hour}:${minutes} ${ampm}`;
  }

  // Fallback: if it's something unexpected, try to at least show the hour
  const hour = parseInt(raw) || 0;
  const ampm = hour >= 12 ? 'pm' : 'am';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12}:00 ${ampm}`;
};

// Helper function to parse time strings in both formats (24-hour and AM/PM)
const parseTimeToHour = (timeStr) => {
  if (!timeStr) return 0;
  
  // Handle 24-hour format like "14:00"
  if (timeStr.includes(':')) {
    return parseInt(timeStr.split(':')[0]);
  }
  
  // Handle AM/PM format like "10AM", "2PM"
  const match = timeStr.match(/(\d+)(AM|PM)/i);
  if (match) {
    let hour = parseInt(match[1]);
    const period = match[2].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return hour;
  }
  
  return parseInt(timeStr) || 0;
};

// Convert time to 24-hour format string for comparison
const normalizeTime = (timeStr) => {
  const hour = parseTimeToHour(timeStr);
  return `${String(hour).padStart(2, '0')}:00`;
};

// Format time for display (converts any format to HH:MM)
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return '';
  // If already in HH:MM format, return as-is
  if (timeStr.includes(':') && timeStr.length >= 5) {
    return timeStr.slice(0, 5);
  }
  // Convert AM/PM to HH:MM
  const hour = parseTimeToHour(timeStr);
  return `${String(hour).padStart(2, '0')}:00`;
};

// Get today's date in local timezone (YYYY-MM-DD)
// Using toISOString() returns UTC which can be 1 day behind in UTC+ timezones
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format time range in 12-hour format with "to"
const formatTimeRange12Hour = (startTime, endTime) => {
  const formatHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12} ${ampm}`;
  };
  const startHour = parseTimeToHour(startTime);
  const endHour = parseTimeToHour(endTime);
  return `${formatHour(startHour)} to ${formatHour(endHour)}`;
};

const isPaymentUnpaid = (status, bookingStatus) => {
  const normalized = String(status || '').toLowerCase();
  const normalizedBookingStatus = String(bookingStatus || '').toLowerCase();
  return ['unpaid', 'pending', 'reserved'].includes(normalized)
    || ['pending', 'reservation'].includes(normalizedBookingStatus);
};

const getPaymentStatusLabel = (status, bookingStatus) => {
  const normalized = String(status || '').toLowerCase();
  const normalizedBookingStatus = String(bookingStatus || '').toLowerCase();
  if (normalized === 'paid') return 'Paid';
  if (normalized === 'pending') return 'Pending';
  if (normalized === 'failed') return 'Failed';
  if (normalized === 'expired') return 'Expired';
  if (normalized === 'reserved') return 'Reserved';
  if (normalized === 'unpaid') return 'Unpaid';
  if (!normalized && ['pending', 'reservation'].includes(normalizedBookingStatus)) return 'Pending';
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Unknown';
};

const getPaymentStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'paid') return 'bg-emerald-500/20 text-emerald-400';
  if (normalized === 'failed' || normalized === 'expired') return 'bg-rose-500/20 text-rose-400';
  return 'bg-amber-500/20 text-amber-400';
};

// Time slots from 6 AM to 10 PM
const TIME_SLOTS = [];
for (let hour = 6; hour <= 21; hour++) {
  const formatHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12} ${ampm}`;
  };
  TIME_SLOTS.push({
    start: `${String(hour).padStart(2, '0')}:00`,
    end: `${String(hour + 1).padStart(2, '0')}:00`,
    label: `${formatHour(hour)} to ${formatHour(hour + 1)}`,
    hour: hour
  });
}

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote },
  { id: 'card', label: 'Card', icon: CreditCard },
  { id: 'ewallet', label: 'eWallet', icon: Smartphone },
  { id: 'online_banking', label: 'Online Banking', icon: Building }
];

const AdminBookingsPage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [courts, setCourts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockOffs, setBlockOffs] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Highlight booking from URL parameter (for redirect from transactions page)
  const [highlightBookingId, setHighlightBookingId] = useState(null);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(false);
  
  // Delete booking confirmation modal (from redirect)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Visual highlight for booking/block-off (orange glow effect)
  const [visualHighlightId, setVisualHighlightId] = useState(null);

  // Keep a ref to the latest selectedDate so WS handlers can access current value
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);
  
  // Multi-slot selection (now supports multiple courts and non-consecutive slots)
  const [selectedSlots, setSelectedSlots] = useState([]); // Array of {courtId, timeSlot, court}
  
  // Walk-in booking modal
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInForm, setWalkInForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    selected_addons: {}, // { item_id: quantity }
    transaction_type: 'with_payment', // 'reservation' or 'with_payment'
    payment_method: 'cash',
    notes: ''
  });
  
  // Admin Reserve modal
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveNote, setReserveNote] = useState('');
  
  // Booking details modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingNotes, setBookingNotes] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState(false);
  const [isEditingAddOns, setIsEditingAddOns] = useState(false);
  const [editingAddOns, setEditingAddOns] = useState({});
  
  // Cancellation modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationNote, setCancellationNote] = useState('');
  const [relatedPackageBookings, setRelatedPackageBookings] = useState([]); // For special package cancellation
  
  // Block-off details modal
  const [showBlockOffModal, setShowBlockOffModal] = useState(false);
  const [selectedBlockOff, setSelectedBlockOff] = useState(null);
  
  // Special Package modal
  const [showSpecialPackageModal, setShowSpecialPackageModal] = useState(false);
  const [specialPackages, setSpecialPackages] = useState([]);
  const [specialPackageForm, setSpecialPackageForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    check_in_date: '',
    check_out_date: '',
    package_id: '',
    selected_addons: {},
    transaction_type: 'with_payment',
    payment_method: 'cash',
    // Multi-day booking selections: array of { date, court_id, time_slots: [] }
    day_bookings: []
  });
  const [currentDayIndex, setCurrentDayIndex] = useState(0); // Which day we're currently configuring
  const [modalAvailability, setModalAvailability] = useState([]); // Available slots for selected date/court
  const [tempDayBooking, setTempDayBooking] = useState({ date: '', court_id: '', time_slots: [] }); // Temp state for current day being edited
  
  // Advanced Admin Reserve modal (standalone)
  const [showAdvancedReserveModal, setShowAdvancedReserveModal] = useState(false);
  const [advancedReserveStep, setAdvancedReserveStep] = useState(1); // 1: Setup, 2: Conflicts, 3: Note
  const [advancedReserveForm, setAdvancedReserveForm] = useState({
    courtSelection: 'all', // 'all' or 'specific'
    selectedCourts: [],
    startDate: getLocalDateString(),
    endDate: getLocalDateString(),
    startTime: '08:00',
    endTime: '17:00',
    note: ''
  });
  const [conflictResults, setConflictResults] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  // Reschedule modal
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '',
    new_court: ''
  });

  // Kiosk transactions state
  const [kioskTransactions, setKioskTransactions] = useState([]);
  const [selectedKioskTransaction, setSelectedKioskTransaction] = useState(null);
  const [showKioskModal, setShowKioskModal] = useState(false);
  const [kioskModalLoading, setKioskModalLoading] = useState(false);
  const [showKioskPaymentForm, setShowKioskPaymentForm] = useState(false);
  const [selectedKioskPaymentMethod, setSelectedKioskPaymentMethod] = useState('cash');
  const [kioskPaymentLoading, setKioskPaymentLoading] = useState(false);

  // Build an index of kiosk transactions for the selected date so the calendar can render quickly.
  // Supports both payload shapes:
  // - booking_infos[].time_slots: ["10AM", "11AM"]
  // - booking_infos[].booking_time_start/booking_time_end: "13:00:00"-"14:00:00"
  // Merges consecutive bookings from the same customer on the same court
  const kioskSlotMetaByKey = useMemo(() => {
    const map = new Map();
    const txns = Array.isArray(kioskTransactions) ? kioskTransactions : [];
    if (txns.length === 0) return map;

    const courtNumberToId = new Map();
    for (const court of courts) {
      const courtNumber = parseInt(String(court?.name || '').match(/\d+/)?.[0] || '0', 10);
      if (Number.isFinite(courtNumber) && courtNumber > 0) {
        courtNumberToId.set(courtNumber, court.court_id);
      }
    }

    const normalizedSlotsFromBookingInfo = (bookingInfo) => {
      const slots = Array.isArray(bookingInfo?.time_slots) ? bookingInfo.time_slots : [];
      if (slots.length > 0) {
        return slots.map((ts) => normalizeTime(ts)).sort();
      }

      if (bookingInfo?.booking_time_start && bookingInfo?.booking_time_end) {
        const startHour = parseTimeToHour(bookingInfo.booking_time_start);
        const endHour = parseTimeToHour(bookingInfo.booking_time_end);
        if (!Number.isFinite(startHour) || !Number.isFinite(endHour) || endHour <= startHour) return [];

        const out = [];
        for (let h = startHour; h < endHour; h++) {
          out.push(`${String(h).padStart(2, '0')}:00`);
        }
        return out;
      }

      return [];
    };

    for (const txn of txns) {
      const bookingInfos = Array.isArray(txn?.booking_infos) ? txn.booking_infos : [];
      if (bookingInfos.length === 0) continue;

      // Group booking infos by court and merge consecutive time ranges
      const bookingsByCourtDate = bookingInfos
        .filter(b => b?.booking_date === selectedDate)
        .reduce((acc, bookingInfo) => {
          const courtNo = bookingInfo?.court_no ?? bookingInfo?.court_id;
          const courtId = courtNumberToId.get(Number(courtNo));
          if (!courtId) return acc;

          const key = `${courtId}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(bookingInfo);
          return acc;
        }, {});

      // For each court, merge consecutive bookings
      for (const [courtId, courtBookings] of Object.entries(bookingsByCourtDate)) {
        // Get all time slots from all bookings for this court
        const allSlots = courtBookings.flatMap(b => normalizedSlotsFromBookingInfo(b));
        if (allSlots.length === 0) continue;

        // Sort and deduplicate
        const uniqueSortedSlots = [...new Set(allSlots)].sort((a, b) => 
          parseInt(a.split(':')[0], 10) - parseInt(b.split(':')[0], 10)
        );

        // Create a merged booking info with all slots
        const mergedBookingInfo = {
          ...courtBookings[0],
          time_slots: uniqueSortedSlots,
          booking_time_start: uniqueSortedSlots[0],
          booking_time_end: `${String(parseInt(uniqueSortedSlots[uniqueSortedSlots.length - 1].split(':')[0], 10) + 1).padStart(2, '0')}:00`
        };

        // Map each slot
        for (let i = 0; i < uniqueSortedSlots.length; i++) {
          const currentHour = parseInt(uniqueSortedSlots[i].split(':')[0], 10);
          const prevHour = i > 0 ? parseInt(uniqueSortedSlots[i - 1].split(':')[0], 10) : null;
          const isFirst = i === 0 || currentHour - prevHour !== 1;

          let span = 1;
          if (isFirst) {
            for (let j = i + 1; j < uniqueSortedSlots.length; j++) {
              const prevH = parseInt(uniqueSortedSlots[j - 1].split(':')[0], 10);
              const currH = parseInt(uniqueSortedSlots[j].split(':')[0], 10);
              if (currH - prevH === 1) span++;
              else break;
            }
          }

          map.set(`${courtId}|${uniqueSortedSlots[i]}`, {
            txn,
            bookingInfo: mergedBookingInfo,
            isFirst,
            span: isFirst ? span : 1,
          });
        }
      }
    }

    return map;
  }, [kioskTransactions, courts, selectedDate]);

  // Default base rate if no pricing rules match
  const DEFAULT_RATE = 500;

  // Format currency with comma separators and centavos
  const formatCurrency = (amount) => {
    return Number(amount).toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Handle URL parameters (for redirect from transactions page)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const highlightParam = searchParams.get('highlight');
    const actionParam = searchParams.get('action');
    
    if (dateParam) {
      const paramDate = new Date(dateParam);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      paramDate.setHours(0, 0, 0, 0);
      
      // If the date parameter is today or in the future, use it; otherwise default to today
      if (paramDate >= today) {
        setSelectedDate(dateParam);
      } else {
        // Date is in the past, default to today
        setSelectedDate(getLocalDateString());
      }
    }
    if (highlightParam) {
      setHighlightBookingId(highlightParam);
    }
    if (actionParam === 'delete') {
      setPendingDeleteAction(true);
    }
  }, [searchParams]);

  // Auto-open delete confirmation modal when highlighted booking is found with delete action
  useEffect(() => {
    if (highlightBookingId && !loading) {
      // First check in bookings array
      let bookingToHighlight = bookings.find(b => b.booking_id === highlightBookingId);
      
      // If not found in bookings, check if it's a block-off
      if (!bookingToHighlight && highlightBookingId.startsWith('block_')) {
        const blockOff = blockOffs.find(b => b.block_id === highlightBookingId);
        if (blockOff) {
          // Convert block-off to booking-like format for the modal
          bookingToHighlight = {
            booking_id: blockOff.block_id,
            court_id: blockOff.court_id,
            date: blockOff.date,
            start_time: blockOff.start_time,
            end_time: blockOff.end_time,
            status: 'reservation',
            customer_name: 'Admin Reservation',
            notes: blockOff.reason || '',
            total_amount: 0,
            payment_status: 'unpaid',
            source: 'block_off',
            is_block_off: true
          };
        }
      }
      
      if (bookingToHighlight) {
        if (pendingDeleteAction) {
          // Show delete confirmation modal
          setBookingToDelete(bookingToHighlight);
          setShowDeleteConfirmModal(true);
        } else {
          // Just open the booking modal for viewing
          setSelectedBooking(bookingToHighlight);
          setBookingNotes(bookingToHighlight.notes || '');
          setShowBookingModal(true);
          
          toast.info('This is the active booking you were trying to delete. You can cancel or manage it here.', {
            duration: 5000
          });
        }
        
        // Clear the URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        newParams.delete('action');
        setSearchParams(newParams, { replace: true });
        setHighlightBookingId(null);
        setPendingDeleteAction(false);
      }
    }
  }, [highlightBookingId, bookings, blockOffs, loading, pendingDeleteAction, searchParams, setSearchParams]);

  // Fetch kiosk transactions using shared token manager
  const fetchKioskTransactions = useCallback(async () => {
    try {
      const txArray = await fetchKioskTransactionsData();
      setKioskTransactions(txArray);
    } catch (error) {
      console.error('Failed to fetch kiosk transactions:', error);
    }
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const startAll = Date.now();
    try {
      if (!silent) console.log('[AdminBookings] fetchData start', { selectedDate });

      const timedFetch = async (url, opts = {}, timeoutMs = 15000) => {
        const t0 = Date.now();
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
          const res = await fetch(url, { ...opts, signal: controller.signal });
          const dur = Date.now() - t0;
          console.log(`[AdminBookings] fetch ${url} -> ${res.status} (${dur}ms)`);
          clearTimeout(timer);
          return res;
        } catch (err) {
          const dur = Date.now() - t0;
          if (err.name === 'AbortError') {
            console.error(`[AdminBookings] fetch ${url} aborted after ${dur}ms`);
          } else {
            console.error(`[AdminBookings] fetch ${url} failed after ${dur}ms`, err);
          }
          clearTimeout(timer);
          throw err;
        }
      };

      const [courtsRes, bookingsRes, addOnsRes, blockOffsRes, pricingRes, packagesRes] = await Promise.all([
        timedFetch(`${API}/admin/courts`, { headers: { 'Authorization': `Bearer ${token}` }}),
        timedFetch(`${API}/admin/bookings?date=${selectedDate}&limit=500`, { headers: { 'Authorization': `Bearer ${token}` }}),
        timedFetch(`${API}/admin/pos/items`, { headers: { 'Authorization': `Bearer ${token}` }}),
        timedFetch(`${API}/admin/block-offs`, { headers: { 'Authorization': `Bearer ${token}` }}),
        timedFetch(`${API}/admin/pricing`, { headers: { 'Authorization': `Bearer ${token}` }}),
        timedFetch(`${API}/special-packages`)
      ]);

      const totalDur = Date.now() - startAll;
      console.log('[AdminBookings] fetchData completed in', totalDur, 'ms');

      if (courtsRes && courtsRes.ok) {
        const courtsData = await courtsRes.json();
        setCourts(courtsData);
      }
      if (bookingsRes) {
        if (bookingsRes.ok) {
          const bookingsData = await bookingsRes.json();
          console.log('[AdminBookings] bookings count:', (bookingsData.bookings || []).length);
          // Log sample of booking ids/dates for debugging missing bookings
          try {
            const sample = (bookingsData.bookings || []).slice(0, 10).map(b => ({ booking_id: b.booking_id, date: b.date, created_at: b.created_at }));
            console.log('[AdminBookings] bookings sample:', sample);
          } catch (e) {
            console.log('[AdminBookings] failed to log booking sample', e);
          }
          setBookings(bookingsData.bookings || []);
        } else {
          const text = await bookingsRes.text().catch(() => '<no body>');
          console.error('[AdminBookings] bookings fetch not ok', bookingsRes.status, text);
        }
      }
      if (addOnsRes && addOnsRes.ok) {
        setAddOns(await addOnsRes.json());
      }
      if (blockOffsRes && blockOffsRes.ok) {
        setBlockOffs(await blockOffsRes.json());
      }
      if (pricingRes && pricingRes.ok) {
        const pricingData = await pricingRes.json();
        setPricingRules(pricingData.filter(r => r.is_active));
      }
      if (packagesRes && packagesRes.ok) {
        setSpecialPackages(await packagesRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      if (!silent) setLoading(false);
    }

    // Fetch kiosk transactions separately so failures don't affect core bookings
    fetchKioskTransactions();
  }, [selectedDate, token, fetchKioskTransactions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get kiosk transaction for a specific court and time slot
  const getKioskTransactionForSlot = (courtId, timeSlot) => {
    const meta = kioskSlotMetaByKey.get(`${courtId}|${timeSlot.start}`);
    if (!meta) return null;
    return { ...meta.txn, currentBookingInfo: meta.bookingInfo, _kioskMeta: meta };
  };

  // Get kiosk transaction span
  const getKioskTransactionSpan = (kioskTxn, currentSlotTime) => {
    if (kioskTxn?._kioskMeta?.span) return kioskTxn._kioskMeta.span;
    if (!kioskTxn?.currentBookingInfo) return 1;

    const info = kioskTxn.currentBookingInfo;
    const slotHour = parseInt(currentSlotTime.split(':')[0], 10);

    const timeSlots = Array.isArray(info?.time_slots) ? info.time_slots : [];
    if (timeSlots.length > 0) {
      const normalizedSlots = timeSlots.map((ts) => normalizeTime(ts)).sort();
      const currentIndex = normalizedSlots.findIndex((s) => parseInt(s.split(':')[0], 10) === slotHour);
      if (currentIndex === -1) return 1;

      let span = 1;
      for (let i = currentIndex + 1; i < normalizedSlots.length; i++) {
        const prevHour = parseInt(normalizedSlots[i - 1].split(':')[0], 10);
        const currHour = parseInt(normalizedSlots[i].split(':')[0], 10);
        if (currHour - prevHour === 1) span++;
        else break;
      }
      return span;
    }

    if (info?.booking_time_start && info?.booking_time_end) {
      const startHour = parseTimeToHour(info.booking_time_start);
      const endHour = parseTimeToHour(info.booking_time_end);
      if (slotHour < startHour || slotHour >= endHour) return 1;
      return Math.max(1, endHour - slotHour);
    }

    return 1;
  };

  // Check if first slot of kiosk transaction
  const isFirstSlotOfKioskTransaction = (courtId, timeSlot) => {
    const meta = kioskSlotMetaByKey.get(`${courtId}|${timeSlot.start}`);
    return Boolean(meta?.isFirst);
  };

  // Handle kiosk transaction click
  const handleKioskTransactionClick = async (transactionCode) => {
    setShowKioskModal(true);
    setKioskModalLoading(true);
    setSelectedKioskTransaction(null);

    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) {
        toast.error('Failed to authenticate with kiosk API');
        setShowKioskModal(false);
        return;
      }

      const url = `${KIOSK_API_BASE}/transactions/${transactionCode}`;
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      setSelectedKioskTransaction(txData);
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      toast.error(`Failed to load transaction: ${error.message}`);
      setShowKioskModal(false);
    } finally {
      setKioskModalLoading(false);
    }
  };

  // Close kiosk modal
  const closeKioskModal = () => {
    setShowKioskModal(false);
    setSelectedKioskTransaction(null);
    setShowKioskPaymentForm(false);
  };

  // Confirm kiosk payment
  const confirmKioskPayment = async (transactionCode, paymentMethod) => {
    if (kioskPaymentLoading) return false;
    
    if (selectedKioskTransaction?.payment_status === 'paid') {
      toast.info('Payment already confirmed');
      return true;
    }
    
    setKioskPaymentLoading(true);
    try {
      const res = await fetch(`${API}/admin/payments/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reference_no: transactionCode,
          transaction_code: transactionCode,
          payment_method: paymentMethod
        })
      });

      if (res.ok) {
        toast.success('Payment confirmed successfully');
        return true;
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.message || 'Failed to confirm payment');
        return false;
      }
    } catch (error) {
      console.error('Error confirming kiosk payment:', error);
      toast.error('Error confirming payment');
      return false;
    } finally {
      setKioskPaymentLoading(false);
    }
  };

  // WebSocket connection for realtime kiosk transaction updates (optional - won't break if unavailable)
  useEffect(() => {
    let ws = null;
    let reconnectTimer;
    let reconnectDelayMs = 2000;
    
    const connect = () => {
      try {
        const wsUrl = getWebSocketUrl();
        console.log('🔌 Attempting WebSocket connection:', wsUrl);
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('✅ WebSocket connected for realtime updates');
          reconnectDelayMs = 2000;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📡 WebSocket message received:', data);
            if (data.type === 'kiosk_transaction_updated' || data.type === 'booking_created' || 
                data.type === 'booking_updated' || data.type === 'payment_confirmed' ||
                data.type === 'booking_deleted' || data.type === 'booking_cancelled' ||
                data.type === 'block_off_created' || data.type === 'block_off_deleted') {
              console.log('🔄 Refreshing data...');

              // DEBUG: log booking payload details when available
              try {
                if (data.booking) {
                  console.log('[AdminBookings][WS] booking event ->', {
                    booking_id: data.booking.booking_id,
                    date: data.booking.date,
                    court_id: data.booking.court_id,
                    time_slots: data.booking.time_slots,
                    status: data.booking.status,
                    payment_status: data.booking.payment_status,
                    created_at: data.booking.created_at,
                    source: data.booking.source
                  });
                }
              } catch (err) {
                console.warn('[AdminBookings][WS] failed to log booking payload', err);
              }

              fetchKioskTransactions();

              // Refresh via REST first, then merge WS booking if it's for the currently selected date
              fetchData(true).then(() => {
                try {
                  if (data.booking && data.booking.date === selectedDateRef.current) {
                    setBookings((prev) => {
                      const exists = (prev || []).some(b => b.booking_id === data.booking.booking_id);
                      if (exists) return prev; // already present
                      // Insert the new booking at the front (most-recent)
                      const newArr = [data.booking, ...(prev || [])];
                      // Keep pagination/limit consistent by slicing to 500 (same as backend limit)
                      return newArr.slice(0, 500);
                    });

                    // Visual highlight so admin notices the new booking
                    setVisualHighlightId(data.booking.booking_id);
                    setTimeout(() => setVisualHighlightId(null), 6000);
                  } else if (data.booking && data.booking.date !== selectedDateRef.current) {
                    // Notify admin a booking for a different date was created
                    console.log(`[AdminBookings] booking created for different date: ${data.booking.date}`);
                  }
                } catch (err) {
                  console.error('[AdminBookings] failed to merge WS booking into state', err);
                }
              }).catch(err => console.warn('[AdminBookings] fetchData after WS failed', err));
            }
          } catch (error) {
            console.error('❌ Failed to parse WebSocket message:', error);
          }
        };
        
        ws.onerror = (error) => {
          console.warn('⚠️ WebSocket connection error (realtime updates disabled):', error.type);
          console.log('💡 This is normal if backend WebSocket is not available. Page will still work with manual refresh.');
        };
        
        ws.onclose = (event) => {
          if (event.wasClean) {
            console.log('🔌 WebSocket disconnected cleanly');
          } else {
            console.warn('⚠️ WebSocket connection lost, attempting reconnect...');
          }
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };
      } catch (error) {
        console.warn('⚠️ WebSocket setup failed (realtime updates disabled):', error.message);
        console.log('💡 Page will continue to work normally with manual refresh.');
      }
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKioskTransactions]);

  // Get price for a specific slot based on pricing rules
  const getPriceForSlot = (courtId, hour) => {
    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayType = isWeekend ? 'weekend' : 'weekday';
    const timeStr = `${String(hour).padStart(2, '0')}:00`;
    
    // Find matching pricing rule
    const slotStartMinutes = hour * 60;
    const matchingRule = pricingRules.find(rule => {
      // Check if rule is active
      if (!rule.is_active) return false;
      
      // Check day type (weekday/weekend) — allow "all" to match any day
      if (rule.day_type && rule.day_type !== 'all' && rule.day_type !== dayType) return false;
      
      // Check time range using minute-level precision
      if (rule.start_time && rule.end_time) {
        const stParts = rule.start_time.split(':');
        const etParts = rule.end_time.split(':');
        const ruleStartMin = parseInt(stParts[0]) * 60 + parseInt(stParts[1] || '0');
        let ruleEndMin = parseInt(etParts[0]) * 60 + parseInt(etParts[1] || '0');
        // For non-zero minutes (e.g. :59), the end is inclusive — convert to exclusive
        if (ruleEndMin % 60 !== 0) ruleEndMin += 1;
        if (slotStartMinutes < ruleStartMin || slotStartMinutes >= ruleEndMin) return false;
      }
      
      // Check court selection
      if (rule.court_selection === 'selected' && rule.selected_courts?.length > 0) {
        if (!rule.selected_courts.includes(courtId)) return false;
      }
      
      return true;
    });
    
    return matchingRule ? matchingRule.price_per_hour : DEFAULT_RATE;
  };

  // Calculate total price for all selected slots
  const calculateTotalPrice = () => {
    return selectedSlots.reduce((total, slot) => {
      const price = getPriceForSlot(slot.courtId, slot.timeSlot.hour);
      return total + price;
    }, 0);
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
    setSelectedSlots([]); // Clear selection when changing date
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(format(date, 'yyyy-MM-dd'));
      setSelectedSlots([]);
      setCalendarOpen(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  // Get block-off for a specific court and time slot
  const getBlockOffForSlot = (courtId, timeSlot) => {
    return blockOffs.find(b => {
      if (b.court_id !== courtId || b.date !== selectedDate) return false;
      const blockStartHour = parseInt(b.start_time.split(':')[0]);
      const blockEndHour = parseInt(b.end_time.split(':')[0]);
      const slotHour = parseInt(timeSlot.start.split(':')[0]);
      // Handle end times like "11:59" by treating them as covering the full hour
      const adjustedEndHour = b.end_time.includes(':59') ? blockEndHour + 1 : blockEndHour;
      return slotHour >= blockStartHour && slotHour < adjustedEndHour;
    });
  };

  // Get block-off span (how many slots it covers)
  const getBlockOffSpan = (blockOff) => {
    const startHour = parseInt(blockOff.start_time.split(':')[0]);
    const endHour = parseInt(blockOff.end_time.split(':')[0]);
    // Handle end times like "11:59" by treating them as covering the full hour
    const adjustedEndHour = blockOff.end_time.includes(':59') ? endHour + 1 : endHour;
    return adjustedEndHour - startHour;
  };

  // Check if this is the first slot of a block-off
  const isFirstSlotOfBlockOff = (courtId, timeSlot) => {
    const blockOff = getBlockOffForSlot(courtId, timeSlot);
    if (!blockOff) return false;
    const blockStartHour = parseInt(blockOff.start_time.split(':')[0]);
    const slotHour = parseInt(timeSlot.start.split(':')[0]);
    return blockStartHour === slotHour;
  };

  // Get booking for a specific court and time slot
  const getBookingForSlot = (courtId, timeSlot) => {
    return bookings.find(b => {
      if (b.court_id !== courtId || b.date !== selectedDate) return false;
      if (b.status === 'cancelled') return false; // Skip cancelled bookings
      
      const slotTime = timeSlot.start; // e.g., "11:00"
      
      // Check time_slots array first (for non-consecutive bookings)
      if (b.time_slots && b.time_slots.length > 0) {
        return b.time_slots.includes(slotTime);
      }
      
      // Fallback to legacy start_time/end_time check
      const bookingStartHour = parseTimeToHour(b.start_time);
      const bookingEndHour = parseTimeToHour(b.end_time);
      const slotHour = parseInt(timeSlot.start.split(':')[0]);
      return slotHour >= bookingStartHour && slotHour < bookingEndHour;
    });
  };

  // Get booking span (how many slots it covers) - returns 1 for non-consecutive individual slots
  const getBookingSpan = (booking, currentSlotTime) => {
    // For non-consecutive bookings, check if this slot is part of a consecutive group
    if (booking.time_slots && booking.time_slots.length > 0) {
      const sortedSlots = [...booking.time_slots].sort((a, b) => 
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      
      const currentHour = parseInt(currentSlotTime.split(':')[0]);
      const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === currentHour);
      
      if (currentIndex === -1) return 1;
      
      // Count consecutive slots starting from current slot
      let span = 1;
      for (let i = currentIndex + 1; i < sortedSlots.length; i++) {
        const prevHour = parseInt(sortedSlots[i - 1].split(':')[0]);
        const currHour = parseInt(sortedSlots[i].split(':')[0]);
        if (currHour - prevHour === 1) {
          span++;
        } else {
          break;
        }
      }
      return span;
    }
    
    // Fallback to legacy calculation
    const startHour = parseTimeToHour(booking.start_time);
    const endHour = parseTimeToHour(booking.end_time);
    return endHour - startHour;
  };

  // Check if this is the first slot of a booking (or first of a consecutive group)
  const isFirstSlotOfBooking = (courtId, timeSlot) => {
    const booking = getBookingForSlot(courtId, timeSlot);
    if (!booking) return false;
    
    const slotHour = parseInt(timeSlot.start.split(':')[0]);
    
    // For non-consecutive bookings, check if this is the first of a consecutive group
    if (booking.time_slots && booking.time_slots.length > 0) {
      const sortedSlots = [...booking.time_slots].sort((a, b) => 
        parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
      );
      
      const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
      if (currentIndex === -1) return false;
      
      // It's first if it's the first slot overall, or if the previous slot is not consecutive
      if (currentIndex === 0) return true;
      const prevHour = parseInt(sortedSlots[currentIndex - 1].split(':')[0]);
      return slotHour - prevHour !== 1;
    }
    
    // Fallback to legacy check
    const bookingStartHour = parseTimeToHour(booking.start_time);
    return bookingStartHour === slotHour;
  };

  // Check if a slot is selected
  const isSlotSelected = (courtId, timeSlot) => {
    return selectedSlots.some(s => s.courtId === courtId && s.timeSlot.start === timeSlot.start);
  };

  // Check if a time slot is in the past
  const isSlotInPast = (timeSlot) => {
    const today = getLocalDateString();
    
    // If selected date is in the future, no slots are in the past
    if (selectedDate > today) return false;
    
    // If selected date is in the past, all slots are in the past
    if (selectedDate < today) return true;
    
    // If selected date is today, check the time
    const now = new Date();
    const currentHour = now.getHours();
    const slotHour = timeSlot.hour;
    
    // Slot is in the past only if its hour has already passed (current hour is still available)
    return slotHour < currentHour;
  };

  // Handle slot click for multi-selection (supports multiple courts and non-consecutive)
  const handleSlotClick = (court, timeSlot) => {
    const booking = getBookingForSlot(court.court_id, timeSlot);
    const blockOff = getBlockOffForSlot(court.court_id, timeSlot);
    
    // If slot is booked, show booking details
    if (booking) {
      handleBookedSlotClick(booking);
      return;
    }
    
    // If slot is blocked, show block-off details
    if (blockOff) {
      setSelectedBlockOff(blockOff);
      setShowBlockOffModal(true);
      return;
    }
    
    // If slot is in the past, don't allow selection
    if (isSlotInPast(timeSlot)) {
      toast.error('Cannot select past time slots');
      return;
    }
    
    const isSelected = isSlotSelected(court.court_id, timeSlot);
    
    if (isSelected) {
      // Deselect this slot
      setSelectedSlots(prev => prev.filter(s => !(s.courtId === court.court_id && s.timeSlot.start === timeSlot.start)));
    } else {
      // Add to selection (allow any court and any time)
      setSelectedSlots(prev => [...prev, { courtId: court.court_id, timeSlot, court }]);
    }
  };

  // Get selected slots grouped by court
  const getSelectedSlotsByCourt = () => {
    const grouped = {};
    selectedSlots.forEach(s => {
      if (!grouped[s.courtId]) {
        grouped[s.courtId] = { court: s.court, slots: [] };
      }
      grouped[s.courtId].slots.push(s.timeSlot);
    });
    // Sort slots within each court
    Object.values(grouped).forEach(g => {
      g.slots.sort((a, b) => a.hour - b.hour);
    });
    return grouped;
  };

  // Get summary of selection for display
  const getSelectionSummary = () => {
    const grouped = getSelectedSlotsByCourt();
    const courts = Object.values(grouped);
    const totalHours = selectedSlots.length;
    return { courts, totalHours };
  };

  // Open walk-in modal with selected slots
  const openWalkInModal = () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }
    
    setWalkInForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      selected_addons: {},
      transaction_type: 'with_payment',
      payment_method: 'cash',
      notes: ''
    });
    setShowWalkInModal(true);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedSlots([]);
  };

  // Handle booked slot click
  const handleBookedSlotClick = async (booking) => {
    try {
      // Fetch fresh booking details to ensure we have the latest data including add-ons
      const response = await fetch(`${API}/admin/bookings/${booking.booking_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const freshBooking = await response.json();
        setSelectedBooking(freshBooking);
        setBookingNotes(freshBooking.notes || '');
      } else {
        // Fallback to the booking from the list if API call fails
        setSelectedBooking(booking);
        setBookingNotes(booking.notes || '');
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
      // Fallback to the booking from the list
      setSelectedBooking(booking);
      setBookingNotes(booking.notes || '');
    }

    setShowPaymentForm(false);
    setIsEditingAddOns(false);
    setEditingAddOns({});
    setShowBookingModal(true);
  };

  // Start editing add-ons
  const startEditingAddOns = () => {
    // Initialize editing state from current booking add-ons
    const currentAddOns = {};
    if (selectedBooking?.add_ons) {
      selectedBooking.add_ons.forEach(addon => {
        currentAddOns[addon.item_id] = addon.quantity || 1;
      });
    }
    setEditingAddOns(currentAddOns);
    setIsEditingAddOns(true);
  };

  // Cancel editing add-ons
  const cancelEditingAddOns = () => {
    setIsEditingAddOns(false);
    setEditingAddOns({});
  };

  // Save edited add-ons
  const saveEditedAddOns = async () => {
    try {
      const addOnsArray = Object.entries(editingAddOns)
        .filter(([_, qty]) => qty > 0)
        .map(([itemId, quantity]) => {
          const addon = addOns.find(a => a.item_id === itemId);
          return {
            item_id: itemId,
            name: addon?.name || 'Add-on',
            quantity: quantity,
            price: addon?.price || 0
          };
        });

      // Recalculate total_amount: court_cost + new add-ons total
      const addOnsTotal = addOnsArray.reduce((sum, a) => sum + (a.price * a.quantity), 0);

      // Determine court cost reliably:
      // - Prefer explicit `court_cost` if present (newer bookings)
      // - Otherwise, derive court cost by subtracting any existing add-ons from `total_amount`
      const existingAddOnsTotal = (selectedBooking?.add_ons || []).reduce((s, ao) => s + ((ao.price || 0) * (ao.quantity || 0)), 0);
      let courtCost;
      if (typeof selectedBooking?.court_cost === 'number' && !isNaN(selectedBooking.court_cost)) {
        courtCost = selectedBooking.court_cost;
      } else {
        const totalAmt = Number(selectedBooking?.total_amount) || 0;
        courtCost = Math.max(0, totalAmt - existingAddOnsTotal);
      }

      const newTotal = courtCost + addOnsTotal;

      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/add-ons`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ add_ons: addOnsArray, total_amount: newTotal, court_cost: courtCost })
      });

      if (res.ok) {
        toast.success('Add-ons updated successfully');
        setIsEditingAddOns(false);
        setEditingAddOns({});
        fetchData(true);
        // Update the selected booking with new add-ons and total
        setSelectedBooking(prev => ({ ...prev, add_ons: addOnsArray, total_amount: newTotal, court_cost: courtCost }));
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to update add-ons');
      }
    } catch (error) {
      toast.error('Error updating add-ons');
    }
  };

  // Calculate editing add-ons total
  const calculateEditingAddOnsTotal = () => {
    return Object.entries(editingAddOns).reduce((sum, [itemId, quantity]) => {
      const item = addOns.find(a => a.item_id === itemId);
      return sum + ((item?.price || 0) * quantity);
    }, 0);
  };

  // Calculate walk-in total using pricing rules
  const calculateWalkInTotal = () => {
    const courtTotal = calculateTotalPrice();
    const addOnsTotal = Object.entries(walkInForm.selected_addons).reduce((sum, [itemId, quantity]) => {
      const item = addOns.find(a => a.item_id === itemId);
      return sum + ((item?.price || 0) * quantity);
    }, 0);
    return courtTotal + addOnsTotal;
  };

  // Calculate total for a specific court's slots
  const calculateCourtTotal = (courtId, slots) => {
    return slots.reduce((total, slot) => {
      return total + getPriceForSlot(courtId, slot.hour);
    }, 0);
  };

  // Process walk-in booking - creates multiple bookings if multiple courts selected
  const processWalkIn = async () => {
    if (!walkInForm.customer_name) {
      toast.error('Please enter customer name');
      return;
    }

    const grouped = getSelectedSlotsByCourt();
    const bookingPromises = [];

    // Determine payment status based on transaction type
    const isReservation = walkInForm.transaction_type === 'reservation';
    const paymentStatus = isReservation ? 'unpaid' : 'paid';
    const paymentMethod = isReservation ? null : walkInForm.payment_method;
    
    // Generate a shared booked_at timestamp for all bookings
    const bookedAt = new Date().toISOString();

    // Create a booking for each court
    for (const [courtId, data] of Object.entries(grouped)) {
      const sortedSlots = data.slots.sort((a, b) => a.hour - b.hour);
      const startHour = sortedSlots[0].hour;
      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
      const courtTotal = calculateCourtTotal(courtId, sortedSlots);
      const addOnsTotal = Object.entries(walkInForm.selected_addons).reduce((sum, [itemId, quantity]) => {
        const item = addOns.find(a => a.item_id === itemId);
        return sum + ((item?.price || 0) * quantity);
      }, 0);

      // Build explicit time_slots array from selected slots (preserves non-consecutive selection)
      const explicitTimeSlots = sortedSlots.map(s => `${String(s.hour).padStart(2, '0')}:00`);
      
      const bookingData = {
        court_id: courtId,
        date: selectedDate,
        time_slots: explicitTimeSlots,  // Send explicit slots to prevent gap-filling
        start_time: `${String(startHour).padStart(2, '0')}:00`,
        end_time: `${String(endHour).padStart(2, '0')}:00`,
        customer_name: walkInForm.customer_name,
        customer_email: walkInForm.customer_email,
        customer_phone: walkInForm.customer_phone,
        duration_hours: sortedSlots.length,
        total_amount: courtTotal + addOnsTotal,
        court_cost: courtTotal,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        booking_type: 'walk_in',
        booked_at: bookedAt,  // Timestamp when booking was created
        add_ons: Object.entries(walkInForm.selected_addons).map(([itemId, quantity]) => {
          const addon = addOns.find(a => a.item_id === itemId);
          return {
            item_id: itemId,
            name: addon?.name || 'Add-on',
            quantity: quantity,
            price: addon?.price || 0
          };
        }),
        notes: walkInForm.notes
      };

      bookingPromises.push(
        fetch(`${API}/admin/bookings/walk-in`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(bookingData)
        })
      );
    }

    try {
      const results = await Promise.all(bookingPromises);
      
      // Check for conflict errors (409)
      const conflictResults = [];
      const successResults = [];
      for (const r of results) {
        if (r.status === 409) {
          const errData = await r.json().catch(() => ({}));
          conflictResults.push(errData.detail || 'Slot already booked');
        } else if (r.ok) {
          successResults.push(r);
        }
      }
      
      if (conflictResults.length > 0) {
        toast.error(`Booking conflict: ${conflictResults[0]}. Refreshing...`);
        clearSelection();
        fetchData(true);
        setShowWalkInModal(false);
        return;
      }
      
      if (successResults.length === results.length) {
        const courtCount = Object.keys(grouped).length;
        const typeLabel = isReservation ? 'Reservation' : 'Walk-in booking';
        toast.success(`${typeLabel}${courtCount > 1 ? 's' : ''} created successfully`);
        setShowWalkInModal(false);
        clearSelection();
        fetchData(true);
      } else {
        toast.error('Some bookings failed to create');
        fetchData(true);
      }
    } catch (error) {
      toast.error('Error processing walk-in');
      fetchData(true);
    }
  };

  // Update booking notes
  const updateBookingNotes = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/notes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: bookingNotes })
      });

      if (res.ok) {
        toast.success('Notes updated');
        fetchData(true);
      }
    } catch (error) {
      toast.error('Failed to update notes');
    }
  };

  // Cancel booking
  // Open cancel modal - find related bookings if special package
  const openCancelModal = async () => {
    setCancellationNote('');
    setRelatedPackageBookings([]);
    
    // If it's a special package booking, fetch all related bookings from backend
    if (selectedBooking.is_special_package && selectedBooking.booked_at) {
      try {
        const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/related`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.related_bookings && data.related_bookings.length > 0) {
            setRelatedPackageBookings(data.related_bookings);
          }
        }
      } catch (error) {
        console.error('Error fetching related bookings:', error);
        // Fallback to current date bookings only
        const related = bookings.filter(b => 
          b.is_special_package && 
          b.booked_at === selectedBooking.booked_at &&
          b.customer_name === selectedBooking.customer_name &&
          b.status !== 'cancelled'
        );
        setRelatedPackageBookings(related);
      }
    }
    
    setShowCancelModal(true);
  };

  // Process cancellation - cancel all related bookings if special package
  const cancelBooking = async () => {
    if (!cancellationNote.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }
    
    try {
      // Determine which bookings to cancel
      const bookingsToCancel = relatedPackageBookings.length > 0 
        ? relatedPackageBookings 
        : [selectedBooking];
      
      const cancelPromises = bookingsToCancel.map(booking =>
        fetch(`${API}/admin/bookings/${booking.booking_id}/cancel`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cancellation_reason: cancellationNote.trim()
          })
        })
      );

      const results = await Promise.all(cancelPromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount === bookingsToCancel.length) {
        toast.success(bookingsToCancel.length > 1 
          ? `All ${successCount} related bookings cancelled`
          : 'Booking cancelled'
        );
        setShowCancelModal(false);
        setShowBookingModal(false);
        setRelatedPackageBookings([]);
        fetchData(true);
      } else {
        toast.error(`Only ${successCount}/${bookingsToCancel.length} bookings cancelled`);
        fetchData(true);
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  // Delete booking confirmation handler (from redirect)
  const confirmDeleteBooking = async () => {
    if (!bookingToDelete) return;
    
    setIsDeleting(true);
    try {
      const bookingId = bookingToDelete.booking_id;
      const isBlockOff = bookingToDelete.is_block_off || bookingToDelete.source === 'block_off' || bookingId?.startsWith('block_');
      
      if (isBlockOff) {
        // For block-offs, delete from block-offs collection
        const deleteRes = await fetch(`${API}/admin/block-offs/${bookingId}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (deleteRes.ok) {
          toast.success('Admin reservation deleted successfully');
        } else {
          const errorData = await deleteRes.json().catch(() => null);
          throw new Error(errorData?.detail || 'Failed to delete admin reservation');
        }
      } else {
        // For regular bookings, first cancel then delete
        const cancelRes = await fetch(`${API}/admin/bookings/${bookingId}/cancel`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cancellation_reason: 'Deleted by admin from transactions page'
          })
        });

        if (cancelRes.ok) {
          // Now delete the booking
          const deleteRes = await fetch(`${API}/admin/transactions/${bookingId}`, {
            method: 'DELETE',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (deleteRes.ok) {
            toast.success('Booking deleted successfully');
          } else {
            // Even if delete fails after cancel, the booking is cancelled
            toast.success('Booking cancelled successfully');
          }
        } else {
          toast.error('Failed to delete booking');
        }
      }
      
      setShowDeleteConfirmModal(false);
      setBookingToDelete(null);
      fetchData(true);
    } catch (error) {
      console.error('Failed to delete booking:', error);
      toast.error(error.message || 'Failed to delete booking');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteConfirmModal = () => {
    // Set visual highlight to show the booking in orange
    if (bookingToDelete) {
      const highlightId = bookingToDelete.booking_id || bookingToDelete.block_id;
      setVisualHighlightId(highlightId);
      
      // Auto-clear highlight after 5 seconds
      setTimeout(() => {
        setVisualHighlightId(null);
      }, 5000);
    }
    
    setShowDeleteConfirmModal(false);
    setBookingToDelete(null);
  };

  // Update payment status (mark as paid)
  const updatePaymentStatus = async () => {
    if (paymentUpdateLoading) return;
    
    setPaymentUpdateLoading(true);
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_status: 'paid',
          payment_method: selectedPaymentMethod,
          status: 'confirmed'
        })
      });

      if (res.ok) {
        toast.success('Payment recorded successfully');
        setShowPaymentForm(false);
        // Update the selected booking locally
        setSelectedBooking(prev => ({
          ...prev,
          payment_status: 'paid',
          payment_method: selectedPaymentMethod,
          status: 'confirmed'
        }));
        fetchData(true);
      } else {
        toast.error('Failed to update payment');
      }
    } catch (error) {
      toast.error('Error updating payment');
    } finally {
      setPaymentUpdateLoading(false);
    }
  };

  // Open reschedule modal
  const openReschedule = () => {
    setRescheduleForm({
      new_date: selectedBooking.date,
      new_time: selectedBooking.start_time,
      new_court: selectedBooking.court_id
    });
    setShowRescheduleModal(true);
  };

  // Process reschedule
  const processReschedule = async () => {
    try {
      const res = await fetch(`${API}/admin/bookings/${selectedBooking.booking_id}/reschedule`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rescheduleForm)
      });

      if (res.ok) {
        toast.success('Booking rescheduled');
        setShowRescheduleModal(false);
        setShowBookingModal(false);
        fetchData(true);
      } else {
        toast.error('Failed to reschedule');
      }
    } catch (error) {
      toast.error('Error rescheduling booking');
    }
  };

  // Open Admin Reserve modal
  const openReserveModal = () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }
    setReserveNote('');
    setShowReserveModal(true);
  };

  // Process Admin Reserve (block-off)
  const processAdminReserve = async () => {
    if (!reserveNote.trim()) {
      toast.error('Please enter a note for this reservation');
      return;
    }

    const grouped = getSelectedSlotsByCourt();
    const reservationPromises = [];

    // Create reservations for each court, grouping consecutive slots
    for (const [courtId, data] of Object.entries(grouped)) {
      const sortedSlots = data.slots.sort((a, b) => a.hour - b.hour);
      
      // Find consecutive groups of slots
      const consecutiveGroups = [];
      let currentGroup = [sortedSlots[0]];
      
      for (let i = 1; i < sortedSlots.length; i++) {
        const prevSlot = sortedSlots[i - 1];
        const currentSlot = sortedSlots[i];
        
        // Check if consecutive (difference of 1 hour)
        if (currentSlot.hour - prevSlot.hour === 1) {
          currentGroup.push(currentSlot);
        } else {
          // Non-consecutive, start a new group
          consecutiveGroups.push(currentGroup);
          currentGroup = [currentSlot];
        }
      }
      // Don't forget the last group
      consecutiveGroups.push(currentGroup);
      
      // Create a reservation for each consecutive group
      for (const group of consecutiveGroups) {
        const startHour = group[0].hour;
        const endHour = group[group.length - 1].hour + 1;
        
        const reservationData = {
          court_id: courtId,
          date: selectedDate,
          start_time: `${String(startHour).padStart(2, '0')}:00`,
          end_time: `${String(endHour).padStart(2, '0')}:00`,
          customer_name: 'Admin Reservation',
          customer_email: '',
          customer_phone: '',
          notes: reserveNote.trim(),
          status: 'reservation',
          payment_status: 'unpaid',
          payment_method: 'N/A',
          total_amount: 0,
          source: 'admin_reservation',
          add_ons: []
        };

        reservationPromises.push(
          fetch(`${API}/admin/bookings`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(reservationData)
          })
        );
      }
    }

    try {
      const results = await Promise.all(reservationPromises);
      
      // Check for conflict errors (409)
      const conflictResults = [];
      const successResults = [];
      for (const r of results) {
        if (r.status === 409) {
          const errData = await r.json().catch(() => ({}));
          conflictResults.push(errData.detail || 'Slot already booked');
        } else if (r.ok) {
          successResults.push(r);
        }
      }
      
      if (conflictResults.length > 0) {
        toast.error(`Reservation conflict: ${conflictResults[0]}. Refreshing...`);
        clearSelection();
        fetchData(true);
        setShowReserveModal(false);
        return;
      }
      
      if (successResults.length === results.length) {
        const reservationCount = reservationPromises.length;
        toast.success(`Admin reservation${reservationCount > 1 ? 's' : ''} created successfully`);
        setShowReserveModal(false);
        clearSelection();
        fetchData(true);
      } else {
        toast.error('Some reservations failed to create');
        fetchData(true);
      }
    } catch (error) {
      toast.error('Error creating admin reservation');
      fetchData(true);
    }
  };

  // Delete block-off
  const deleteBlockOff = async (blockOffData = null) => {
    const blockOff = blockOffData || selectedBlockOff;
    if (!blockOff) return;
    if (!confirm('Are you sure you want to remove this admin reservation?')) return;
    
    try {
      const res = await fetch(`${API}/admin/bookings/${blockOff.booking_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Admin reservation removed');
        setShowBlockOffModal(false);
        setSelectedBlockOff(null);
        setShowBookingModal(false);
        setSelectedBooking(null);
        fetchData(true);
      } else {
        toast.error('Failed to remove reservation');
      }
    } catch (error) {
      toast.error('Error removing reservation');
    }
  };

  // Advanced Admin Reserve functions
  const openAdvancedReserve = () => {
    setAdvancedReserveForm({
      courtSelection: 'all',
      selectedCourts: [],
      startDate: selectedDate,
      endDate: selectedDate,
      startTime: '08:00',
      endTime: '17:00',
      note: ''
    });
    setAdvancedReserveStep(1);
    setConflictResults(null);
    setShowAdvancedReserveModal(true);
  };

  const checkConflicts = async () => {
    setCheckingConflicts(true);
    try {
      const courtIds = advancedReserveForm.courtSelection === 'all' 
        ? courts.filter(c => c.status === 'active' || !c.status).map(c => c.court_id)
        : advancedReserveForm.selectedCourts;
      
      if (courtIds.length === 0) {
        toast.error('Please select at least one court');
        setCheckingConflicts(false);
        return;
      }
      
      const res = await fetch(`${API}/admin/block-offs/check-conflicts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          court_ids: courtIds,
          start_date: advancedReserveForm.startDate,
          end_date: advancedReserveForm.endDate,
          start_time: advancedReserveForm.startTime,
          end_time: advancedReserveForm.endTime
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConflictResults(data);
        setAdvancedReserveStep(2);
      } else {
        toast.error('Failed to check conflicts');
      }
    } catch (error) {
      toast.error('Error checking conflicts');
    }
    setCheckingConflicts(false);
  };

  const processAdvancedReserve = async () => {
    if (!advancedReserveForm.note.trim()) {
      toast.error('Please enter a note for the reservation');
      return;
    }
    
    // Get courts without conflicts
    const courtsToBlock = conflictResults?.courts
      .filter(c => !c.has_conflicts)
      .map(c => c.court_id) || [];
    
    if (courtsToBlock.length === 0) {
      toast.error('No courts available to block (all have conflicts)');
      return;
    }
    
    // Generate dates
    const dates = conflictResults?.dates || [advancedReserveForm.startDate];
    
    try {
      const blockOffPromises = [];
      
      for (const courtId of courtsToBlock) {
        for (const date of dates) {
          blockOffPromises.push(
            fetch(`${API}/admin/block-offs`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                court_id: courtId,
                date: date,
                start_time: advancedReserveForm.startTime,
                end_time: advancedReserveForm.endTime,
                reason: advancedReserveForm.note.trim()
              })
            })
          );
        }
      }
      
      const results = await Promise.all(blockOffPromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} block-off${successCount > 1 ? 's' : ''} successfully`);
        setShowAdvancedReserveModal(false);
        fetchData(true);
      } else {
        toast.error('Failed to create block-offs');
      }
    } catch (error) {
      toast.error('Error creating block-offs');
    }
  };

  // ==================== SPECIAL PACKAGE FUNCTIONS ====================
  
  const getSelectedPackage = () => {
    return specialPackages.find(p => p.package_id === specialPackageForm.package_id);
  };

  const resetSpecialPackageForm = () => {
    setSpecialPackageForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      check_in_date: '',
      check_out_date: '',
      package_id: '',
      selected_addons: {},
      transaction_type: 'with_payment',
      payment_method: 'cash',
      day_bookings: []
    });
    setCurrentDayIndex(0);
    setModalAvailability([]);
    setTempDayBooking({ date: '', court_id: '', time_slots: [] });
  };

  // Fetch availability for modal's selected date and court
  const fetchModalAvailability = async (date, courtId) => {
    if (!date || !courtId) {
      setModalAvailability([]);
      return;
    }
    
    try {
      // Get existing bookings for this date and court
      const existingBookings = bookings.filter(b => 
        b.date === date && 
        b.court_id === courtId && 
        b.status !== 'cancelled'
      );
      
      // Get block-offs for this date and court
      const existingBlockOffs = blockOffs.filter(b => 
        b.date === date && 
        b.court_id === courtId
      );
      
      // Build list of booked slots
      const bookedSlots = new Set();
      
      existingBookings.forEach(booking => {
        const slots = booking.time_slots || [];
        if (slots.length === 0 && booking.start_time && booking.end_time) {
          const startH = parseInt(booking.start_time.split(':')[0]);
          const endH = parseInt(booking.end_time.split(':')[0]);
          for (let h = startH; h < endH; h++) {
            bookedSlots.add(`${String(h).padStart(2, '0')}:00`);
          }
        } else {
          slots.forEach(s => bookedSlots.add(s));
        }
      });
      
      existingBlockOffs.forEach(blockOff => {
        const startH = parseInt(blockOff.start_time.split(':')[0]);
        const endH = parseInt(blockOff.end_time.split(':')[0]);
        for (let h = startH; h < endH; h++) {
          bookedSlots.add(`${String(h).padStart(2, '0')}:00`);
        }
      });
      
      // Check if selected date is today to determine past slots
      const now = new Date();
      const selectedDateObj = new Date(date);
      const isToday = selectedDateObj.toDateString() === now.toDateString();
      const isPastDate = selectedDateObj < new Date(now.toDateString());
      const currentHour = now.getHours();
      
      // Generate available slots (6 AM to 10 PM)
      const available = [];
      for (let hour = 6; hour <= 21; hour++) {
        const timeStr = `${String(hour).padStart(2, '0')}:00`;
        
        let isPast = false;
        if (isPastDate) {
          isPast = true;
        } else if (isToday && hour <= currentHour) {
          isPast = true;
        }
        
        available.push({
          time: timeStr,
          available: !bookedSlots.has(timeStr) && !isPast,
          isPast: isPast,
          isBooked: bookedSlots.has(timeStr)
        });
      }
      
      setModalAvailability(available);
    } catch (error) {
      console.error('Error fetching modal availability:', error);
      setModalAvailability([]);
    }
  };

  // Toggle slot selection for temp day booking
  const toggleTempDaySlot = (timeStr) => {
    setTempDayBooking(prev => {
      const currentSlots = prev.time_slots || [];
      const isSelected = currentSlots.includes(timeStr);
      
      if (isSelected) {
        return { ...prev, time_slots: currentSlots.filter(s => s !== timeStr) };
      } else {
        return { ...prev, time_slots: [...currentSlots, timeStr].sort() };
      }
    });
  };

  // Add current temp day booking to the list
  const addDayBooking = () => {
    const pkg = getSelectedPackage();
    if (!pkg) return;
    
    if (!tempDayBooking.date || !tempDayBooking.court_id || tempDayBooking.time_slots.length === 0) {
      toast.error('Please select date, court, and time slots');
      return;
    }
    
    if (tempDayBooking.time_slots.length !== pkg.hours_per_day) {
      toast.error(`Please select exactly ${pkg.hours_per_day} hour${pkg.hours_per_day > 1 ? 's' : ''} for this package`);
      return;
    }
    
    // Check if date is already booked
    const existingDay = specialPackageForm.day_bookings.find(d => d.date === tempDayBooking.date);
    if (existingDay) {
      toast.error('This date is already selected. Remove it first to change.');
      return;
    }
    
    setSpecialPackageForm(prev => ({
      ...prev,
      day_bookings: [...prev.day_bookings, { ...tempDayBooking }]
    }));
    
    // Reset temp booking
    setTempDayBooking({ date: '', court_id: '', time_slots: [] });
    setModalAvailability([]);
  };

  // Remove a day booking
  const removeDayBooking = (index) => {
    setSpecialPackageForm(prev => ({
      ...prev,
      day_bookings: prev.day_bookings.filter((_, i) => i !== index)
    }));
  };

  // Check if booking date is within check-in/check-out range
  const isDateInRange = (date) => {
    if (!specialPackageForm.check_in_date || !specialPackageForm.check_out_date) return false;
    return date >= specialPackageForm.check_in_date && date <= specialPackageForm.check_out_date;
  };

  // Generate dates between check-in and check-out
  const getDateRange = () => {
    if (!specialPackageForm.check_in_date || !specialPackageForm.check_out_date) return [];
    const dates = [];
    let current = new Date(specialPackageForm.check_in_date);
    const end = new Date(specialPackageForm.check_out_date);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const getMaxBookableDays = () => {
    const pkg = getSelectedPackage();
    if (!pkg) return 0;
    
    const dateRange = getDateRange();
    // Maximum bookable days is the lesser of: package days OR available dates in range
    return Math.min(pkg.days, dateRange.length);
  };

  const validateSpecialPackageSlots = () => {
    const pkg = getSelectedPackage();
    if (!pkg) return { valid: false, message: 'Please select a package' };
    
    // Validate check-in/check-out dates
    if (!specialPackageForm.check_in_date || !specialPackageForm.check_out_date) {
      return { valid: false, message: 'Please set check-in and check-out dates' };
    }
    
    // Check if at least one day booking exists
    if (specialPackageForm.day_bookings.length === 0) {
      return { valid: false, message: 'Please add at least one day booking' };
    }
    
    const maxDays = getMaxBookableDays();
    if (specialPackageForm.day_bookings.length > maxDays) {
      return { valid: false, message: `Maximum ${maxDays} day(s) can be booked for this date range` };
    }
    
    // Validate each day booking has correct hours
    for (const dayBooking of specialPackageForm.day_bookings) {
      if (dayBooking.time_slots.length !== pkg.hours_per_day) {
        return { valid: false, message: `Each day must have exactly ${pkg.hours_per_day} hour(s)` };
      }
    }
    
    return { valid: true, message: '' };
  };

  const calculateSpecialPackageTotal = () => {
    const pkg = getSelectedPackage();
    if (!pkg) return 0;
    
    let total = pkg.price;
    
    // Add add-ons
    Object.entries(specialPackageForm.selected_addons).forEach(([itemId, qty]) => {
      const addon = addOns.find(a => a.item_id === itemId);
      if (addon && qty > 0) {
        total += addon.price * qty;
      }
    });
    
    return total;
  };

  const handleSpecialPackageSubmit = async () => {
    const validation = validateSpecialPackageSlots();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    
    if (!specialPackageForm.customer_name.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    const pkg = getSelectedPackage();
    if (!pkg) return;
    
    try {
      // Create bookings for each day in day_bookings
      const bookingPromises = [];
      const isReservation = specialPackageForm.transaction_type === 'reservation';
      
      // Generate a shared booked_at timestamp for all bookings in this special package
      const bookedAt = new Date().toISOString();
      
      for (let i = 0; i < specialPackageForm.day_bookings.length; i++) {
        const dayBooking = specialPackageForm.day_bookings[i];
        const sortedSlots = [...dayBooking.time_slots].sort();
        const startTime = sortedSlots[0];
        const lastHour = parseInt(sortedSlots[sortedSlots.length - 1].split(':')[0]);
        const endTime = `${String(lastHour + 1).padStart(2, '0')}:00`;
        
        // Prepare add-ons for first day only
        const addOnsForBooking = i === 0
          ? Object.entries(specialPackageForm.selected_addons)
              .filter(([_, qty]) => qty > 0)
              .map(([itemId, qty]) => {
                const addon = addOns.find(a => a.item_id === itemId);
                return { item_id: itemId, name: addon?.name, quantity: qty, price: addon?.price };
              })
          : [];
        
        bookingPromises.push(
          fetch(`${API}/admin/bookings`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              venue_id: 'venue_camsur',
              court_id: dayBooking.court_id,
              date: dayBooking.date,
              time_slots: sortedSlots,
              start_time: startTime,
              end_time: endTime,
              duration_hours: pkg.hours_per_day,
              customer_name: specialPackageForm.customer_name.trim(),
              customer_email: specialPackageForm.customer_email.trim(),
              customer_phone: specialPackageForm.customer_phone.trim(),
              check_in_date: specialPackageForm.check_in_date,
              check_out_date: specialPackageForm.check_out_date,
              special_package_id: pkg.package_id,
              special_package_name: pkg.name,
              is_special_package: true,
              booked_at: bookedAt,  // Shared timestamp for all bookings in this package
              transaction_type: specialPackageForm.transaction_type,
              payment_method: isReservation ? null : specialPackageForm.payment_method,
              payment_status: isReservation ? 'unpaid' : 'paid',
              status: isReservation ? 'reservation' : 'confirmed',
              total_amount: i === 0 ? calculateSpecialPackageTotal() : 0,
              equipment_rentals: addOnsForBooking,
              notes: `Special Package: ${pkg.name} | Check-in: ${specialPackageForm.check_in_date} | Check-out: ${specialPackageForm.check_out_date} | Day ${i + 1} of ${specialPackageForm.day_bookings.length}`
            })
          })
        );
      }
      
      const results = await Promise.all(bookingPromises);
      const successCount = results.filter(r => r.ok).length;
      
      if (successCount === specialPackageForm.day_bookings.length) {
        toast.success(`Special package booking created successfully! (${successCount} day${successCount > 1 ? 's' : ''})`);
        setShowSpecialPackageModal(false);
        resetSpecialPackageForm();
        fetchData(true);
      } else {
        toast.error(`Some bookings failed. ${successCount}/${specialPackageForm.day_bookings.length} days created.`);
        fetchData(true);
      }
    } catch (error) {
      console.error('Error creating special package booking:', error);
      toast.error('Failed to create special package booking');
    }
  };

  const selectionSummary = getSelectionSummary();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Court Availability</h1>
            <p className={`${theme.textMuted} mt-1`}>Click time slots to select • Click again to deselect</p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Special Package Button - always visible */}
            <Button 
              onClick={() => {
                resetSpecialPackageForm();
                setShowSpecialPackageModal(true);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="special-package-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Special Package
            </Button>
            
            <div className={`flex items-center gap-1 ${theme.card} rounded-lg p-1`}>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => navigateDate(-1)}
                className={`h-8 w-8 ${theme.textMuted}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`px-3 py-1 ${theme.text} font-medium min-w-[160px] ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    {formatDisplayDate(selectedDate)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-auto p-0 ${theme.card}`} align="center">
                  <CalendarComponent
                    mode="single"
                    selected={new Date(selectedDate)}
                    onSelect={handleDateSelect}
                    initialFocus
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => navigateDate(1)}
                className={`h-8 w-8 ${theme.textMuted}`}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button 
              onClick={openAdvancedReserve}
              className={`${isDarkMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
              data-testid="admin-reserve-btn"
            >
              <ShieldBan className="h-4 w-4 mr-2" />
              Admin Reserve
            </Button>
          </div>
        </div>

        {/* Selection Bar - Shows when slots are selected */}
        {selectedSlots.length > 0 && (
          <Card className={theme.selectionBar}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1">
                  <p className={`${theme.selectionText} font-semibold`}>
                    {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected ({selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''})
                  </p>
                  <div className={`${theme.selectionTextMuted} text-sm space-y-1 mt-1`}>
                    {selectionSummary.courts.map(({ court, slots }) => (
                      <div key={court.court_id}>
                        <span className="font-medium">{court.name}:</span>{' '}
                        {slots.map(s => s.label.split('-')[0]).join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    className={`${isDarkMode ? 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20' : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'}`}
                    onClick={clearSelection}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Selection
                  </Button>
                  <Button 
                    variant="outline"
                    className={`${isDarkMode ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/20' : 'border-amber-600 text-amber-700 hover:bg-amber-50'}`}
                    onClick={openReserveModal}
                  >
                    <ShieldBan className="h-4 w-4 mr-2" />
                    Admin Reserve
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={openWalkInModal}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Process Walk-in
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar Grid */}
        <Card className={`${theme.card} overflow-hidden`}>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse table-fixed">
                  <thead>
                    <tr className={`border-b ${theme.calendarBorder}`}>
                      <th className={`p-4 text-left text-sm font-medium ${theme.calendarTimeText} w-28 ${theme.calendarTimeBg} sticky left-0`}>
                        TIME
                      </th>
                      {courts.map((court) => (
                        <th key={court.court_id} className={`p-4 text-center text-sm font-medium ${theme.tableCell} ${theme.calendarTimeBg}`} style={{ width: `${(100 - 10) / courts.length}%` }}>
                          <div className="font-semibold">{court.name.toUpperCase()}</div>
                          <div className={`text-xs ${theme.textSubtle} font-normal`}>PICKLEBALL</div>
                          <div className={`text-xs font-normal mt-1 ${
                            court.status === 'active' ? 'text-emerald-500' :
                            court.status === 'maintenance' ? 'text-amber-500' :
                            court.status === 'inactive' ? 'text-rose-500' :
                            'text-emerald-500'
                          }`}>
                            {court.status === 'active' || !court.status ? '● Active' :
                             court.status === 'maintenance' ? '● Maintenance' :
                             '● Inactive'}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map((slot) => (
                      <tr key={slot.start} className={`border-b ${theme.calendarRowBorder}`}>
                        <td className={`p-3 text-sm ${theme.calendarTimeText} ${theme.calendarTimeBg} font-medium sticky left-0`}>
                          {slot.label}
                        </td>
                        {courts.map((court) => {
                          const booking = getBookingForSlot(court.court_id, slot);
                          const blockOff = getBlockOffForSlot(court.court_id, slot);
                          const kioskTxn = getKioskTransactionForSlot(court.court_id, slot);
                          const isFirstSlotBooking = isFirstSlotOfBooking(court.court_id, slot);
                          const isFirstSlotBlock = isFirstSlotOfBlockOff(court.court_id, slot);
                          const isFirstSlotKiosk = isFirstSlotOfKioskTransaction(court.court_id, slot);
                          const bookingSpan = booking ? getBookingSpan(booking, slot.start) : 1;
                          const blockSpan = blockOff ? getBlockOffSpan(blockOff) : 1;
                          const kioskSpan = kioskTxn ? getKioskTransactionSpan(kioskTxn, slot.start) : 1;
                          const isSelected = isSlotSelected(court.court_id, slot);
                          const isCourtAvailable = court.status === 'active' || !court.status;
                          const isPast = isSlotInPast(slot);
                          
                          // Skip rendering if this cell is covered by a multi-hour booking from a previous row
                          if (booking && !isFirstSlotBooking) {
                            return null;
                          }
                          
                          // Skip rendering if this cell is covered by a multi-hour block-off from a previous row
                          // The cell should be skipped regardless of whether there's also a booking
                          // (because the block-off's rowSpan from the first slot already covers this cell)
                          if (blockOff && !isFirstSlotBlock) {
                            return null;
                          }
                          
                          // Skip if covered by kiosk transaction
                          if (kioskTxn && !isFirstSlotKiosk) {
                            return null;
                          }
                          
                          return (
                            <td 
                              key={court.court_id} 
                              className="p-1 relative"
                              rowSpan={booking ? bookingSpan : (kioskTxn && isFirstSlotKiosk ? kioskSpan : (blockOff && isFirstSlotBlock ? blockSpan : 1))}
                              style={{ height: '72px', verticalAlign: 'top' }}
                            >
                              {kioskTxn && isFirstSlotKiosk ? (
                                <div 
                                  onClick={() => handleKioskTransactionClick(kioskTxn.transaction_code)}
                                  className={`h-full ${
                                    kioskTxn.payment_status?.toLowerCase() === 'paid'
                                      ? (isDarkMode ? 'bg-purple-500/20 border-2 border-purple-400/50' : 'bg-purple-100 border-2 border-purple-400')
                                      : (isDarkMode ? 'bg-cyan-500/20 border-2 border-cyan-400/50' : 'bg-cyan-100 border-2 border-cyan-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(kioskSpan * 72) - 8}px` }}
                                >
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-purple-400' : 'text-cyan-400') : 'text-gray-900'} font-semibold text-xs leading-tight`}>
                                    {(() => {
                                      const info = kioskTxn.currentBookingInfo;
                                      // Display the full merged time range
                                      if (info?.time_slots && info.time_slots.length > 0) {
                                        const normalizedSlots = info.time_slots.map(ts => normalizeTime(ts)).sort();
                                        const slotHour = parseInt(slot.start.split(':')[0]);
                                        const currentIndex = normalizedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
                                        
                                        if (currentIndex !== -1) {
                                          // Find the end of consecutive slots from current position
                                          let endHour = slotHour + 1;
                                          for (let i = currentIndex + 1; i < normalizedSlots.length; i++) {
                                            const prevH = parseInt(normalizedSlots[i - 1].split(':')[0]);
                                            const currH = parseInt(normalizedSlots[i].split(':')[0]);
                                            if (currH - prevH === 1) {
                                              endHour = currH + 1;
                                            } else {
                                              break;
                                            }
                                          }
                                          return `${formatKioskTimeDisplay(slot.start)} - ${formatKioskTimeDisplay(`${String(endHour).padStart(2, '0')}:00`)}`;
                                        }
                                      }

                                      if (info?.booking_time_start && info?.booking_time_end) {
                                        return `${formatKioskTimeDisplay(info.booking_time_start)} - ${formatKioskTimeDisplay(info.booking_time_end)}`;
                                      }

                                      return formatKioskTimeDisplay(slot.start);
                                    })()}
                                  </div>
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-purple-300' : 'text-cyan-300') : 'text-gray-800'} text-xs mt-1 leading-tight`}>
                                    Court Rental: Kiosk Booking
                                  </div>
                                  <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-purple-200' : 'text-cyan-200') : 'text-gray-700'} text-xs font-medium mt-1 leading-tight truncate`}>
                                    {kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'Paid:' : 'Reservation:'} {kioskTxn.name || 'Guest'}
                                  </div>
                                  {(() => {
                                    // Show all courts/times if customer has multiple bookings on the same date
                                    const allBookings = (kioskTxn.booking_infos || []).filter(b => b.booking_date === selectedDate);
                                    if (allBookings.length > 1) {
                                      const courtNos = allBookings.map(b => b.court_no || b.court_id).filter(c => c != null);
                                      const uniqueCourts = [...new Set(courtNos)].sort((a, b) => a - b);
                                      return (
                                        <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-purple-200' : 'text-cyan-200') : 'text-gray-700'} text-[10px] opacity-90 leading-tight mt-1`}>
                                          <div className="font-semibold">Multiple Courts:</div>
                                          <div className="truncate">Courts {uniqueCourts.join(', ')}</div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className={`${isDarkMode ? (kioskTxn.payment_status?.toLowerCase() === 'paid' ? 'text-purple-200' : 'text-cyan-200') : 'text-gray-700'} text-[10px] opacity-75 leading-tight truncate`}>
                                        ({kioskTxn.contact_number || '-'})
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : booking ? (
                                <div 
                                  onClick={() => handleBookedSlotClick(booking)}
                                  className={`h-full ${
                                    visualHighlightId === booking.booking_id
                                      ? 'bg-orange-500/40 border-2 border-orange-500 ring-4 ring-orange-500/50 animate-pulse'
                                      : booking.is_special_package
                                        ? (isDarkMode ? 'bg-orange-500/20 border-2 border-orange-400/50' : 'bg-orange-100 border-2 border-orange-400')
                                        : isPaymentUnpaid(booking.payment_status, booking.status)
                                          ? (isDarkMode ? 'bg-blue-500/20 border-2 border-blue-400/50' : 'bg-blue-100 border-2 border-blue-400')
                                          : (isDarkMode ? 'bg-emerald-500/20 border-2 border-emerald-400/50' : 'bg-emerald-100 border-2 border-emerald-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(bookingSpan * 72) - 8}px` }}
                                >
                                  <div className={`${
                                    booking.is_special_package 
                                      ? (isDarkMode ? 'text-orange-400' : 'text-orange-900')
                                      : isDarkMode 
                                        ? (isPaymentUnpaid(booking.payment_status, booking.status) ? 'text-blue-400' : 'text-emerald-400') 
                                        : 'text-gray-900'
                                  } font-semibold text-xs leading-tight`}>
                                    {/* Show time for this consecutive group */}
                                    {(() => {
                                      if (booking.time_slots && booking.time_slots.length > 0) {
                                        const slotHour = parseInt(slot.start.split(':')[0]);
                                        const sortedSlots = [...booking.time_slots].sort((a, b) => 
                                          parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])
                                        );
                                        const currentIndex = sortedSlots.findIndex(s => parseInt(s.split(':')[0]) === slotHour);
                                        
                                        // Find the end of this consecutive group
                                        let endHour = slotHour + 1;
                                        for (let i = currentIndex + 1; i < sortedSlots.length; i++) {
                                          const prevH = parseInt(sortedSlots[i - 1].split(':')[0]);
                                          const currH = parseInt(sortedSlots[i].split(':')[0]);
                                          if (currH - prevH === 1) {
                                            endHour = currH + 1;
                                          } else {
                                            break;
                                          }
                                        }
                                        return `${formatTimeRange12Hour(slot.start, `${String(endHour).padStart(2, '0')}:00`)}`;
                                      }
                                      return `${formatTimeRange12Hour(booking.start_time, booking.end_time)}`;
                                    })()}
                                  </div>
                                  <div className={`${
                                    booking.is_special_package 
                                      ? (isDarkMode ? 'text-orange-300' : 'text-orange-800')
                                      : isDarkMode 
                                        ? (isPaymentUnpaid(booking.payment_status, booking.status) ? 'text-blue-300' : 'text-emerald-300') 
                                        : 'text-gray-800'
                                  } text-xs mt-1 leading-tight`}>
                                    {booking.is_special_package 
                                      ? 'Court Rental: Special Package'
                                      : booking.booking_type === 'walk_in' 
                                        ? 'Court Rental: Walk-in' 
                                        : 'Court Rental: Online Booking'
                                    }
                                  </div>
                                  <div className={`${
                                    booking.is_special_package 
                                      ? (isDarkMode ? 'text-orange-200' : 'text-orange-700')
                                      : isDarkMode 
                                        ? (isPaymentUnpaid(booking.payment_status, booking.status) ? 'text-blue-200' : 'text-emerald-200') 
                                        : 'text-gray-700'
                                  } text-xs font-medium mt-1 leading-tight`}>
                                    {booking.is_special_package
                                      ? `${booking.transaction_type === 'reservation' ? 'Reservation' : 'Paid'}: ${booking.customer_name || booking.user?.name || 'Guest'}`
                                      : isPaymentUnpaid(booking.payment_status, booking.status)
                                        ? `Reservation: ${booking.customer_name || booking.user?.name || 'Guest'}`
                                        : `Paid: ${booking.customer_name || booking.user?.name || 'Guest'}`
                                    }
                                    {booking.booking_type !== 'walk_in' && !booking.is_special_package && booking.user?.email && (
                                      <span className="ml-1 opacity-75">({booking.user.email})</span>
                                    )}
                                  </div>
                                </div>
                              ) : blockOff && isFirstSlotBlock ? (
                                <div 
                                  onClick={() => { setSelectedBlockOff(blockOff); setShowBlockOffModal(true); }}
                                  className={`h-full ${
                                    visualHighlightId === blockOff.block_id
                                      ? 'bg-orange-500/40 border-2 border-orange-500 ring-4 ring-orange-500/50 animate-pulse'
                                      : (isDarkMode ? 'bg-rose-500/20 border-2 border-rose-400/50' : 'bg-rose-100 border-2 border-rose-400')
                                  } rounded-lg p-2 cursor-pointer hover:opacity-80 transition-colors overflow-hidden`}
                                  style={{ height: `${(blockSpan * 72) - 8}px` }}
                                >
                                  <div className={`${isDarkMode ? 'text-rose-400' : 'text-gray-900'} font-semibold text-xs leading-tight`}>
                                    {formatTimeRange12Hour(blockOff.start_time, blockOff.end_time)}
                                  </div>
                                  <div className={`${isDarkMode ? 'text-rose-300' : 'text-gray-800'} text-xs mt-1 flex items-center gap-1 leading-tight`}>
                                    <ShieldBan className="h-3 w-3" />
                                    Admin Reserve
                                  </div>
                                  <div className={`${isDarkMode ? 'text-rose-200' : 'text-gray-700'} text-xs font-medium mt-1 leading-tight`}>
                                    {blockOff.reason}
                                  </div>
                                </div>
                              ) : !isCourtAvailable ? (
                                <div className={`h-[64px] rounded-lg ${isDarkMode ? 'bg-slate-700/30 border-slate-600/30' : 'bg-gray-200 border-gray-300'} border flex items-center justify-center`}>
                                  <span className={`${theme.textSubtle} text-xs`}>Unavailable</span>
                                </div>
                              ) : isPast ? (
                                <div className={`h-[64px] rounded-lg ${theme.calendarPast} border flex items-center justify-center cursor-not-allowed opacity-50`}>
                                  <span className={`${theme.calendarPastText} text-xs`}>Past</span>
                                </div>
                              ) : (
                                <div 
                                  onClick={() => handleSlotClick(court, slot)}
                                  className={`h-[64px] rounded-lg cursor-pointer transition-all ${
                                    isSelected 
                                      ? 'bg-amber-500/30 border-2 border-amber-400' 
                                      : `border ${theme.calendarSlotBorder} ${theme.calendarSlotHover}`
                                  }`}
                                >
                                  {isSelected && (
                                    <div className="h-full flex items-center justify-center">
                                      <CheckCircle className="h-5 w-5 text-amber-500" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Walk-in Booking Modal */}
        {showWalkInModal && selectedSlots.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg ${theme.modalBg} my-8`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Walk-in Booking</CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowWalkInModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Slot Info - Shows all selected courts */}
                <div className={`p-3 ${theme.selectionBar} rounded-lg`}>
                  <p className={`${theme.selectionText} font-medium mb-2`}>
                    {formatDisplayDate(selectedDate)} • {selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1">
                    {selectionSummary.courts.map(({ court, slots }) => {
                      const sortedSlots = slots.sort((a, b) => a.hour - b.hour);
                      const startHour = sortedSlots[0].hour;
                      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
                      const formatHour = (h) => h === 12 ? '12PM' : h > 12 ? `${h - 12}PM` : `${h}AM`;
                      return (
                        <div key={court.court_id} className={`${theme.selectionTextMuted} text-sm`}>
                          <span className="font-semibold">{court.name}:</span> {formatHour(startHour)} - {formatHour(endHour)} ({slots.length}hr)
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.tableCell} flex items-center gap-2`}>
                    <User className="h-4 w-4" /> Customer Information
                  </h3>
                  <div>
                    <Label className={`${theme.textMuted} text-xs`}>Name *</Label>
                    <Input
                      value={walkInForm.customer_name}
                      onChange={(e) => setWalkInForm(p => ({ ...p, customer_name: e.target.value }))}
                      className={theme.input}
                      placeholder="Customer name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>Email</Label>
                      <Input
                        type="email"
                        value={walkInForm.customer_email}
                        onChange={(e) => setWalkInForm(p => ({ ...p, customer_email: e.target.value }))}
                        className={theme.input}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <Label className={`${theme.textMuted} text-xs`}>Phone</Label>
                      <Input
                        value={walkInForm.customer_phone}
                        onChange={(e) => setWalkInForm(p => ({ ...p, customer_phone: e.target.value }))}
                        className={theme.input}
                        placeholder="+63 XXX XXX XXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Add-ons */}
                <div>
                  <Label className={`${theme.textMuted} text-xs mb-2 block`}>Add-ons</Label>
                  <div className={`${theme.input} border rounded-md p-2 max-h-40 overflow-y-auto space-y-1`}>
                    {addOns.length === 0 ? (
                      <p className={`${theme.textSubtle} text-sm p-2`}>No add-ons available</p>
                    ) : (
                      addOns.slice(0, 10).map((item) => (
                        <div key={item.item_id} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'}`}>
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="checkbox"
                              checked={!!walkInForm.selected_addons[item.item_id]}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setWalkInForm(p => ({ 
                                    ...p, 
                                    selected_addons: { ...p.selected_addons, [item.item_id]: 1 }
                                  }));
                                } else {
                                  setWalkInForm(p => {
                                    const newAddons = { ...p.selected_addons };
                                    delete newAddons[item.item_id];
                                    return { ...p, selected_addons: newAddons };
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span className={`${theme.text} text-sm`}>{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {walkInForm.selected_addons[item.item_id] && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentQty = walkInForm.selected_addons[item.item_id] || 1;
                                    if (currentQty > 1) {
                                      setWalkInForm(p => ({
                                        ...p,
                                        selected_addons: { ...p.selected_addons, [item.item_id]: currentQty - 1 }
                                      }));
                                    }
                                  }}
                                  className={`w-7 h-7 rounded border flex items-center justify-center text-sm font-medium transition-colors ${
                                    isDarkMode 
                                      ? 'border-slate-600 bg-slate-700 hover:bg-slate-600 text-white' 
                                      : 'border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className={`w-8 text-center text-sm font-medium ${theme.text}`}>
                                  {walkInForm.selected_addons[item.item_id] || 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentQty = walkInForm.selected_addons[item.item_id] || 1;
                                    if (currentQty < 99) {
                                      setWalkInForm(p => ({
                                        ...p,
                                        selected_addons: { ...p.selected_addons, [item.item_id]: currentQty + 1 }
                                      }));
                                    }
                                  }}
                                  className={`w-7 h-7 rounded border flex items-center justify-center text-sm font-medium transition-colors ${
                                    isDarkMode 
                                      ? 'border-slate-600 bg-slate-700 hover:bg-slate-600 text-white' 
                                      : 'border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  <Plus className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                            <span className="text-emerald-500 text-sm w-20 text-right">₱{formatCurrency(item.price)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Add-ons Total */}
                  {Object.keys(walkInForm.selected_addons).length > 0 && (
                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                      <div className="flex justify-between items-center">
                        <span className={`${theme.textMuted} font-medium`}>Add-ons Total</span>
                        <span className="text-emerald-500 font-semibold text-lg">
                          ₱{formatCurrency(
                            Object.entries(walkInForm.selected_addons).reduce((sum, [itemId, quantity]) => {
                              const addonItem = addOns.find(a => a.item_id === itemId);
                              return sum + ((addonItem?.price || 0) * (quantity || 1));
                            }, 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Transaction Type */}
                <div>
                  <Label className={`${theme.textMuted} text-xs mb-2 block`}>Transaction Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <label 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        walkInForm.transaction_type === 'reservation'
                          ? 'border-amber-500 bg-amber-500/20'
                          : `${isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-300 hover:border-gray-400'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="transaction_type"
                        value="reservation"
                        checked={walkInForm.transaction_type === 'reservation'}
                        onChange={(e) => setWalkInForm(p => ({ ...p, transaction_type: e.target.value }))}
                        className="text-amber-500"
                      />
                      <div>
                        <span className={`${walkInForm.transaction_type === 'reservation' ? 'text-amber-400' : theme.text} font-medium`}>Reservation</span>
                        <p className={`text-xs ${theme.textMuted}`}>Mark as unpaid</p>
                      </div>
                    </label>
                    <label 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        walkInForm.transaction_type === 'with_payment'
                          ? 'border-emerald-500 bg-emerald-500/20'
                          : `${isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-300 hover:border-gray-400'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="transaction_type"
                        value="with_payment"
                        checked={walkInForm.transaction_type === 'with_payment'}
                        onChange={(e) => setWalkInForm(p => ({ ...p, transaction_type: e.target.value }))}
                        className="text-emerald-500"
                      />
                      <div>
                        <span className={`${walkInForm.transaction_type === 'with_payment' ? 'text-emerald-400' : theme.text} font-medium`}>With Payment</span>
                        <p className={`text-xs ${theme.textMuted}`}>Mark as paid</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Payment Method - Only show when With Payment is selected */}
                {walkInForm.transaction_type === 'with_payment' && (
                  <div>
                    <Label className={`${theme.textMuted} text-xs mb-2 block`}>Payment Method</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {PAYMENT_METHODS.map((method) => {
                        const Icon = method.icon;
                        return (
                          <button
                            key={method.id}
                            onClick={() => setWalkInForm(p => ({ ...p, payment_method: method.id }))}
                            className={`p-3 rounded-lg border text-center transition-colors ${
                              walkInForm.payment_method === method.id
                                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-600'
                                : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`
                            }`}
                          >
                            <Icon className="h-5 w-5 mx-auto mb-1" />
                            <span className="text-xs">{method.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className={`${theme.textMuted} text-xs`}>Notes</Label>
                  <textarea
                    value={walkInForm.notes}
                    onChange={(e) => setWalkInForm(p => ({ ...p, notes: e.target.value }))}
                    className={`w-full h-16 px-3 py-2 rounded-md ${theme.input} border resize-none`}
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Total */}
                <div className={`p-4 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                  <div className="flex justify-between items-center">
                    <span className={theme.tableCell}>Court Rental ({selectionSummary.totalHours}hr)</span>
                    <span className={theme.text}>₱{formatCurrency(calculateTotalPrice())}</span>
                  </div>
                  {Object.keys(walkInForm.selected_addons).length > 0 && (
                    <>
                      {Object.entries(walkInForm.selected_addons).map(([itemId, quantity]) => {
                        const item = addOns.find(a => a.item_id === itemId);
                        if (!item) return null;
                        return (
                          <div key={itemId} className="flex justify-between items-center mt-1">
                            <span className={theme.tableCell}>{item.name} x{quantity}</span>
                            <span className={theme.text}>₱{formatCurrency(item.price * quantity)}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  <div className={`flex justify-between items-center mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                    <span className={`${theme.text} font-semibold`}>Total</span>
                    <span className="text-emerald-500 font-bold text-xl">₱{formatCurrency(calculateWalkInTotal())}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowWalkInModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className={`flex-1 ${walkInForm.transaction_type === 'reservation' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`} 
                    onClick={processWalkIn}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {walkInForm.transaction_type === 'reservation' ? 'Create Reservation' : 'Process Payment'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Booking Details Modal */}
        {showBookingModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} my-8`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Booking Details</CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowBookingModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Booking Source Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                  selectedBooking.is_special_package 
                    ? (isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700')
                    : selectedBooking.booking_type === 'walk_in'
                      ? (isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                      : (isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700')
                }`}>
                  <Package className="h-3 w-3" />
                  Source: {selectedBooking.is_special_package 
                    ? 'Special Package' 
                    : selectedBooking.booking_type === 'walk_in' 
                      ? 'Walk-in Booking' 
                      : 'Online Booking'
                  }
                </div>

                {/* Booking Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <FileText className="h-4 w-4" /> Booking Information
                  </h3>
                  <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Booking ID</span>
                      <span className={`${theme.text} font-mono text-sm`}>{selectedBooking.booking_id?.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Customer</span>
                      <span className={theme.text}>{selectedBooking.customer_name || selectedBooking.user?.name || 'Guest'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Court</span>
                      <span className={theme.text}>{courts.find(c => c.court_id === selectedBooking.court_id)?.name || selectedBooking.court_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Date & Time</span>
                      <span className={theme.text}>{formatDisplayDate(selectedBooking.date)} • {formatTimeRange12Hour(selectedBooking.start_time, selectedBooking.end_time)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Booked Date & Time</span>
                      <span className={theme.text}>
                        {selectedBooking.booked_at 
                          ? new Date(selectedBooking.booked_at).toLocaleString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: 'numeric', minute: '2-digit', hour12: true
                            })
                          : selectedBooking.created_at 
                            ? new Date(selectedBooking.created_at).toLocaleString('en-US', { 
                                year: 'numeric', month: 'short', day: 'numeric',
                                hour: 'numeric', minute: '2-digit', hour12: true
                              })
                            : 'N/A'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textMuted}>Status</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        selectedBooking.status === 'cancelled' 
                          ? 'bg-rose-500/20 text-rose-400' 
                          : isPaymentUnpaid(selectedBooking.payment_status, selectedBooking.status) || selectedBooking.status === 'reservation'
                            ? 'bg-rose-500/20 text-rose-400'
                            : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {selectedBooking.status === 'cancelled' 
                          ? 'Cancelled'
                          : isPaymentUnpaid(selectedBooking.payment_status, selectedBooking.status) || selectedBooking.status === 'reservation'
                            ? 'Reservation'
                            : 'Confirmed'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add-ons Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                      <Package className="h-4 w-4" /> Add-ons
                    </h3>
                    {isPaymentUnpaid(selectedBooking.payment_status, selectedBooking.status) && selectedBooking.status !== 'cancelled' && !isEditingAddOns && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className={`h-7 text-xs ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
                        onClick={startEditingAddOns}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Edit Items
                      </Button>
                    )}
                  </div>
                  
                  {isEditingAddOns ? (
                    // Edit Mode
                    <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-3`}>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {addOns.map((item) => (
                          <div key={item.item_id} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'hover:bg-slate-600/50' : 'hover:bg-gray-200'}`}>
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="checkbox"
                                checked={!!editingAddOns[item.item_id]}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingAddOns(p => ({ ...p, [item.item_id]: 1 }));
                                  } else {
                                    setEditingAddOns(p => {
                                      const newAddons = { ...p };
                                      delete newAddons[item.item_id];
                                      return newAddons;
                                    });
                                  }
                                }}
                                className="rounded"
                              />
                              <span className={`${theme.text} text-sm`}>{item.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {editingAddOns[item.item_id] && (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentQty = editingAddOns[item.item_id] || 1;
                                      if (currentQty > 1) {
                                        setEditingAddOns(p => ({ ...p, [item.item_id]: currentQty - 1 }));
                                      }
                                    }}
                                    className={`w-7 h-7 rounded border flex items-center justify-center text-sm font-medium transition-colors ${
                                      isDarkMode 
                                        ? 'border-slate-600 bg-slate-700 hover:bg-slate-600 text-white' 
                                        : 'border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className={`w-8 text-center text-sm font-medium ${theme.text}`}>
                                    {editingAddOns[item.item_id] || 1}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentQty = editingAddOns[item.item_id] || 1;
                                      if (currentQty < 99) {
                                        setEditingAddOns(p => ({ ...p, [item.item_id]: currentQty + 1 }));
                                      }
                                    }}
                                    className={`w-7 h-7 rounded border flex items-center justify-center text-sm font-medium transition-colors ${
                                      isDarkMode 
                                        ? 'border-slate-600 bg-slate-700 hover:bg-slate-600 text-white' 
                                        : 'border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              <span className="text-emerald-500 text-sm w-20 text-right">₱{formatCurrency(item.price)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Editing Total */}
                      <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                        <span className={`${theme.textMuted} font-medium`}>Add-ons Total</span>
                        <span className="text-emerald-500 font-semibold">₱{formatCurrency(calculateEditingAddOnsTotal())}</span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                          onClick={cancelEditingAddOns}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={saveEditedAddOns}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode - Check both add_ons and equipment_rentals (for special packages)
                    (() => {
                      const bookingAddOns = selectedBooking.add_ons?.length > 0 
                        ? selectedBooking.add_ons 
                        : selectedBooking.equipment_rentals?.length > 0 
                          ? selectedBooking.equipment_rentals 
                          : [];
                      
                      return bookingAddOns.length > 0 ? (
                        <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                          {bookingAddOns.map((addon, index) => {
                            const addonItem = addOns.find(a => a.item_id === addon.item_id);
                            const quantity = addon.quantity || 1;
                            const price = addon.price || addonItem?.price || 0;
                            const name = addon.name || addonItem?.name || addon.item_id;
                            return (
                              <div key={index} className="flex justify-between">
                                <span className={theme.textMuted}>
                                  {name} x{quantity}
                                </span>
                                <span className={theme.text}>₱{formatCurrency(price * quantity)}</span>
                              </div>
                            );
                          })}
                          <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                            <span className={`${theme.textMuted} font-medium`}>Add-ons Total</span>
                            <span className="text-emerald-500 font-semibold">
                              ₱{formatCurrency(bookingAddOns.reduce((sum, addon) => {
                                const addonItem = addOns.find(a => a.item_id === addon.item_id);
                                const price = addon.price || addonItem?.price || 0;
                                return sum + (price * (addon.quantity || 1));
                              }, 0))}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3`}>
                          <p className={`${theme.textSubtle} text-sm text-center`}>No add-ons</p>
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* Payment Info */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <CreditCard className="h-4 w-4" /> Payment Information
                  </h3>
                  {(() => {
                    // Calculate base amount and add-ons separately
                    const bookingAddOns = selectedBooking.add_ons?.length > 0 
                      ? selectedBooking.add_ons 
                      : selectedBooking.equipment_rentals?.length > 0 
                        ? selectedBooking.equipment_rentals 
                        : [];
                    
                    const addOnsTotal = bookingAddOns.reduce((sum, addon) => {
                      const addonItem = addOns.find(a => a.item_id === addon.item_id);
                      const price = addon.price || addonItem?.price || 0;
                      return sum + (price * (addon.quantity || 1));
                    }, 0);
                    
                    // For special packages, look up the actual package price
                    // Use `court_cost` if available (new bookings). For legacy bookings
                    // where `court_cost` is missing but `total_amount` already includes add-ons,
                    // derive the base court amount by subtracting the add-ons total from total_amount.
                    let baseAmount;
                    const bookingAddOnsForCalc = bookingAddOns;
                    const addOnsTotalForCalc = addOnsTotal;

                    if (selectedBooking.is_special_package && selectedBooking.special_package_id) {
                      const packageData = specialPackages.find(p => p.package_id === selectedBooking.special_package_id);
                      if (packageData) {
                        baseAmount = packageData.price;
                      } else if (selectedBooking.court_cost != null) {
                        baseAmount = selectedBooking.court_cost;
                      } else {
                        const tot = Number(selectedBooking.total_amount) || 0;
                        baseAmount = Math.max(0, tot - addOnsTotalForCalc);
                      }
                    } else if (selectedBooking.court_cost != null) {
                      // New booking: court_cost is stored explicitly
                      baseAmount = selectedBooking.court_cost;
                    } else {
                      // Legacy booking: total_amount may include add-ons — subtract them to get court cost
                      const tot = Number(selectedBooking.total_amount) || 0;
                      baseAmount = Math.max(0, tot - addOnsTotalForCalc);
                    }
                    
                    const computedTotal = baseAmount + addOnsTotal;
                    
                    return (
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        {/* Court Rental or Special Package Base */}
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>
                            {selectedBooking.is_special_package 
                              ? (selectedBooking.special_package_name || 'Special Package')
                              : 'Court Rental'
                            }
                          </span>
                          <span className={theme.text}>₱{formatCurrency(baseAmount)}</span>
                        </div>
                        
                        {/* Add-ons */}
                        {bookingAddOns.length > 0 && (
                          <div className="flex justify-between">
                            <span className={theme.textMuted}>Add-ons</span>
                            <span className={theme.text}>₱{formatCurrency(addOnsTotal)}</span>
                          </div>
                        )}
                        
                        {/* Total Amount */}
                        <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                          <span className={`${theme.textMuted} font-medium`}>Total Amount</span>
                          <span className="text-emerald-500 font-semibold">
                            ₱{formatCurrency(computedTotal)}
                          </span>
                        </div>
                        
                        {/* Payment Method */}
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Payment Method</span>
                          <span className={`${theme.text} capitalize`}>{selectedBooking.payment_method || 'N/A'}</span>
                        </div>
                        
                        {/* Payment Status */}
                        <div className="flex justify-between items-center">
                          <span className={theme.textMuted}>Payment Status</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getPaymentStatusClass(selectedBooking.payment_status)}`}>
                            {getPaymentStatusLabel(selectedBooking.payment_status, selectedBooking.status)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Pay Now Button - Only show when unpaid */}
                  {isPaymentUnpaid(selectedBooking.payment_status, selectedBooking.status) && !showPaymentForm && (
                    <Button 
                      size="sm" 
                      className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        setSelectedPaymentMethod('cash');
                        setShowPaymentForm(true);
                      }}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay Now
                    </Button>
                  )}
                  
                  {/* Payment Form - Show when Pay Now is clicked */}
                    {showPaymentForm && (
                      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'} space-y-3`}>
                        <Label className={`${theme.text} text-sm`}>Select Payment Method</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {PAYMENT_METHODS.map((method) => {
                            const Icon = method.icon;
                            return (
                              <button
                                key={method.id}
                                onClick={() => setSelectedPaymentMethod(method.id)}
                                className={`p-2 rounded-lg border text-center transition-colors ${
                                  selectedPaymentMethod === method.id
                                    ? 'border-emerald-500 bg-emerald-500/20 text-emerald-500'
                                    : isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                <Icon className="h-4 w-4 mx-auto mb-1" />
                                <span className="text-xs">{method.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                            onClick={() => setShowPaymentForm(false)}
                          >
                            Cancel
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={paymentUpdateLoading}
                            onClick={updatePaymentStatus}
                          >
                            {paymentUpdateLoading ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Processing...
                              </div>
                            ) : (
                              'Save Payment'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                    <FileText className="h-4 w-4" /> Notes
                  </h3>
                  <textarea
                    value={bookingNotes}
                    onChange={(e) => setBookingNotes(e.target.value)}
                    className={`w-full h-20 px-3 py-2 rounded-md resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                    placeholder="Add notes to this booking..."
                  />
                  <Button size="sm" variant="outline" className={isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300'} onClick={updateBookingNotes}>
                    Save Notes
                  </Button>
                </div>

                {/* Action Buttons */}
                {selectedBooking.status !== 'cancelled' && (
                  <div className={`grid grid-cols-2 gap-2 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                    <Button variant="outline" className={isDarkMode ? 'border-slate-600 text-white' : 'border-gray-300'} onClick={openReschedule}>
                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                      Reschedule
                    </Button>
                    {selectedBooking.source === 'block_off' ? (
                      <Button 
                        variant="outline" 
                        className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10" 
                        onClick={() => deleteBlockOff(selectedBooking)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove Booking
                      </Button>
                    ) : (
                      <Button variant="outline" className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10" onClick={openCancelModal}>
                        <XCircle className="h-4 w-4 mr-1" />
                        Cancel Booking
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reschedule Modal */}
        {showRescheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader>
                <CardTitle className={theme.text}>Reschedule Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <Label className={theme.text}>New Date</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.new_date}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_date: e.target.value }))}
                    className={theme.input}
                  />
                </div>
                <div>
                  <Label className={theme.text}>New Time</Label>
                  <select
                    value={rescheduleForm.new_time}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_time: e.target.value }))}
                    className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {TIME_SLOTS.map(slot => (
                      <option key={slot.start} value={slot.start}>{slot.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className={theme.text}>New Court</Label>
                  <select
                    value={rescheduleForm.new_court}
                    onChange={(e) => setRescheduleForm(p => ({ ...p, new_court: e.target.value }))}
                    className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    {courts.map(court => (
                      <option key={court.court_id} value={court.court_id}>{court.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowRescheduleModal(false)}>
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={processReschedule}>
                    Confirm
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin Reserve Modal */}
        {showReserveModal && selectedSlots.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowReserveModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Slot Info */}
                <div className={`p-3 ${isDarkMode ? 'bg-amber-500/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border rounded-lg`}>
                  <p className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700'} font-medium mb-2`}>
                    {formatDisplayDate(selectedDate)} • {selectionSummary.totalHours} hour{selectionSummary.totalHours > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1">
                    {selectionSummary.courts.map(({ court, slots }) => {
                      const sortedSlots = slots.sort((a, b) => a.hour - b.hour);
                      const startHour = sortedSlots[0].hour;
                      const endHour = sortedSlots[sortedSlots.length - 1].hour + 1;
                      const formatHour = (h) => h === 12 ? '12PM' : h > 12 ? `${h - 12}PM` : `${h}AM`;
                      return (
                        <div key={court.court_id} className={`${isDarkMode ? 'text-amber-300' : 'text-amber-600'} text-sm`}>
                          <span className="font-semibold">{court.name}:</span> {formatHour(startHour)} - {formatHour(endHour)} ({slots.length}hr)
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={`${theme.textMuted} text-sm`}>
                  These time slots will be blocked and unavailable for customer booking.
                </div>

                {/* Note (Required) */}
                <div>
                  <Label className={theme.text}>
                    Note <span className="text-rose-400">*</span>
                  </Label>
                  <textarea
                    value={reserveNote}
                    onChange={(e) => setReserveNote(e.target.value)}
                    className={`w-full h-24 px-3 py-2 rounded-md resize-none mt-1 border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Enter reason for blocking these slots (required)..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowReserveModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={processAdminReserve}
                    disabled={!reserveNote.trim()}
                  >
                    <ShieldBan className="h-4 w-4 mr-2" />
                    Block Slots
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Block-Off Details Modal */}
        {showBlockOffModal && selectedBlockOff && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve Details
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => { setShowBlockOffModal(false); setSelectedBlockOff(null); }} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Block-off Info */}
                <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Court</span>
                    <span className={theme.text}>{courts.find(c => c.court_id === selectedBlockOff.court_id)?.name || selectedBlockOff.court_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Date</span>
                    <span className={theme.text}>{selectedBlockOff.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textMuted}>Time</span>
                    <span className={theme.text}>{formatKioskTimeDisplay(selectedBlockOff.start_time)} - {formatKioskTimeDisplay(selectedBlockOff.end_time)}</span>
                  </div>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label className={theme.text}>Note</Label>
                  <div className={`p-3 ${isDarkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-700'} border rounded-lg`}>
                    {selectedBlockOff.reason}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => { setShowBlockOffModal(false); setSelectedBlockOff(null); }}
                  >
                    Close
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
                    onClick={deleteBlockOff}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Block
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cancellation Modal */}
        {showCancelModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg my-8 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <XCircle className="h-5 w-5 text-rose-400" />
                  Cancel {relatedPackageBookings.length > 1 ? 'Special Package' : 'Booking'}
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => { setShowCancelModal(false); setRelatedPackageBookings([]); }} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Special Package Warning */}
                {relatedPackageBookings.length > 1 && (
                  <div className={`p-3 ${isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'} border rounded-lg`}>
                    <p className={`${isDarkMode ? 'text-orange-400' : 'text-orange-700'} font-medium flex items-center gap-2`}>
                      <Package className="h-4 w-4" />
                      Special Package Booking
                    </p>
                    <p className={`${isDarkMode ? 'text-orange-300' : 'text-orange-600'} text-sm mt-1`}>
                      This will cancel all {relatedPackageBookings.length} bookings in this special package.
                    </p>
                  </div>
                )}

                {/* Booking Summary */}
                <div className={`p-3 ${isDarkMode ? 'bg-rose-500/10 border-rose-500/30' : 'bg-rose-50 border-rose-200'} border rounded-lg`}>
                  <p className={`${isDarkMode ? 'text-rose-400' : 'text-rose-700'} font-medium`}>
                    {selectedBooking.customer_name || selectedBooking.user?.name || 'Guest'}
                  </p>
                  {selectedBooking.booked_at && (
                    <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'} text-xs mt-1`}>
                      Booked: {new Date(selectedBooking.booked_at).toLocaleString('en-US', { 
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true
                      })}
                    </p>
                  )}
                </div>

                {/* List of all bookings to be cancelled */}
                {relatedPackageBookings.length > 1 ? (
                  <div className="space-y-2">
                    <Label className={theme.text}>Bookings to be cancelled ({relatedPackageBookings.length})</Label>
                    <div className={`max-h-48 overflow-y-auto space-y-2 p-2 ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'} rounded-lg`}>
                      {relatedPackageBookings.map((booking, idx) => (
                        <div key={booking.booking_id} className={`p-2 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-white'} border ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className={`${theme.text} font-medium text-sm`}>
                                Day {idx + 1}: {booking.date}
                              </p>
                              <p className={`${theme.textMuted} text-xs`}>
                                {courts.find(c => c.court_id === booking.court_id)?.name || booking.court_id}
                              </p>
                              <p className={`${theme.textMuted} text-xs`}>
                                {formatKioskTimeDisplay(booking.start_time)} - {formatKioskTimeDisplay(booking.end_time)}
                                {booking.time_slots && booking.time_slots.length > 1 && (
                                  <span className="ml-1">({booking.time_slots.length} hours)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={`p-3 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg`}>
                    <p className={`${theme.textMuted} text-sm`}>
                      {courts.find(c => c.court_id === selectedBooking.court_id)?.name} • {formatDisplayDate(selectedBooking.date)}
                    </p>
                    <p className={`${theme.textMuted} text-sm`}>
                      {formatKioskTimeDisplay(selectedBooking.start_time)} - {formatKioskTimeDisplay(selectedBooking.end_time)}
                    </p>
                  </div>
                )}

                <div className={`${theme.textMuted} text-sm`}>
                  This action cannot be undone. The time slot{relatedPackageBookings.length > 1 ? 's' : ''} will become available for booking again.
                </div>

                {/* Cancellation Reason (Required) */}
                <div>
                  <Label className={theme.text}>
                    Cancellation Reason <span className="text-rose-400">*</span>
                  </Label>
                  <textarea
                    value={cancellationNote}
                    onChange={(e) => setCancellationNote(e.target.value)}
                    className={`w-full h-24 px-3 py-2 rounded-md resize-none mt-1 border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Enter reason for cancellation (required)..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => { setShowCancelModal(false); setRelatedPackageBookings([]); }}
                  >
                    Go Back
                  </Button>
                  <Button 
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={cancelBooking}
                    disabled={!cancellationNote.trim()}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {relatedPackageBookings.length > 1 
                      ? `Cancel All (${relatedPackageBookings.length})`
                      : 'Confirm Cancellation'
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Admin Reserve Modal */}
        {showAdvancedReserveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-lg ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <ShieldBan className="h-5 w-5 text-amber-400" />
                  Admin Reserve
                  <span className={`text-sm font-normal ${theme.textMuted}`}>
                    - Step {advancedReserveStep} of 3
                  </span>
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => setShowAdvancedReserveModal(false)} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Step 1: Setup */}
                {advancedReserveStep === 1 && (
                  <>
                    {/* Court Selection */}
                    <div>
                      <Label className={theme.text}>Court Selection</Label>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => setAdvancedReserveForm(p => ({ ...p, courtSelection: 'all', selectedCourts: [] }))}
                          className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                            advancedReserveForm.courtSelection === 'all'
                              ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                              : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`
                          }`}
                        >
                          All Courts
                        </button>
                        <button
                          onClick={() => setAdvancedReserveForm(p => ({ ...p, courtSelection: 'specific' }))}
                          className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                            advancedReserveForm.courtSelection === 'specific'
                              ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                              : `${isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`
                          }`}
                        >
                          Specific Courts
                        </button>
                      </div>
                      
                      {advancedReserveForm.courtSelection === 'specific' && (
                        <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'border-slate-600 bg-slate-700/30' : 'border-gray-200 bg-gray-50'} max-h-32 overflow-y-auto`}>
                          {courts.filter(c => c.status === 'active' || !c.status).map(court => (
                            <label key={court.court_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${isDarkMode ? 'hover:bg-slate-600/50' : 'hover:bg-gray-100'}`}>
                              <input
                                type="checkbox"
                                checked={advancedReserveForm.selectedCourts.includes(court.court_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAdvancedReserveForm(p => ({ ...p, selectedCourts: [...p.selectedCourts, court.court_id] }));
                                  } else {
                                    setAdvancedReserveForm(p => ({ ...p, selectedCourts: p.selectedCourts.filter(id => id !== court.court_id) }));
                                  }
                                }}
                                className="rounded"
                              />
                              <span className={theme.text}>{court.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Start Date</Label>
                        <Input
                          type="date"
                          value={advancedReserveForm.startDate}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, startDate: e.target.value }))}
                          className={`mt-1 ${theme.input}`}
                        />
                      </div>
                      <div>
                        <Label className={theme.text}>End Date</Label>
                        <Input
                          type="date"
                          value={advancedReserveForm.endDate}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, endDate: e.target.value }))}
                          className={`mt-1 ${theme.input}`}
                        />
                      </div>
                    </div>

                    {/* Time Range */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Start Time</Label>
                        <select
                          value={advancedReserveForm.startTime}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, startTime: e.target.value }))}
                          className={`w-full h-10 px-3 rounded-md mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        >
                          {TIME_SLOTS.map(slot => (
                            <option key={slot.start} value={slot.start}>{slot.label.split('-')[0]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className={theme.text}>End Time</Label>
                        <select
                          value={advancedReserveForm.endTime}
                          onChange={(e) => setAdvancedReserveForm(p => ({ ...p, endTime: e.target.value }))}
                          className={`w-full h-10 px-3 rounded-md mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        >
                          {TIME_SLOTS.map(slot => (
                            <option key={slot.end} value={slot.end}>{slot.label.split('-')[1]}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowAdvancedReserveModal(false)}>
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={checkConflicts}
                        disabled={checkingConflicts || (advancedReserveForm.courtSelection === 'specific' && advancedReserveForm.selectedCourts.length === 0)}
                      >
                        {checkingConflicts ? 'Checking...' : 'Next: Check Conflicts'}
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 2: Conflict Results */}
                {advancedReserveStep === 2 && conflictResults && (
                  <>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                      <p className={`${theme.text} font-medium`}>
                        {conflictResults.dates.length} day{conflictResults.dates.length > 1 ? 's' : ''} • {conflictResults.time_range}
                      </p>
                      <p className={`${theme.textMuted} text-sm`}>
                        {conflictResults.dates[0]}{conflictResults.dates.length > 1 ? ` to ${conflictResults.dates[conflictResults.dates.length - 1]}` : ''}
                      </p>
                    </div>

                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {conflictResults.courts.map(court => (
                        <div key={court.court_id} className={`p-3 rounded-lg border ${
                          court.has_conflicts 
                            ? (isDarkMode ? 'border-rose-500/50 bg-rose-500/10' : 'border-rose-300 bg-rose-50')
                            : (isDarkMode ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-emerald-300 bg-emerald-50')
                        }`}>
                          <div className="flex items-center gap-2 mb-2">
                            {court.has_conflicts ? (
                              <AlertCircle className="h-4 w-4 text-rose-400" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-emerald-400" />
                            )}
                            <span className={`font-medium ${court.has_conflicts ? 'text-rose-400' : 'text-emerald-400'}`}>
                              {court.court_name}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              court.has_conflicts 
                                ? (isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-600')
                                : (isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-600')
                            }`}>
                              {court.has_conflicts ? `${court.conflicts.length} conflict${court.conflicts.length > 1 ? 's' : ''}` : 'Clear'}
                            </span>
                          </div>
                          {court.has_conflicts && (
                            <div className="space-y-1 ml-6">
                              {court.conflicts.slice(0, 5).map((conflict, idx) => (
                                <div key={idx} className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                  <span className={`font-medium ${theme.textMuted}`}>{conflict.date}</span>
                                  <span className={theme.textMuted}> • {conflict.time}</span>
                                  <div className={`text-xs ${theme.textSubtle}`}>{conflict.description}</div>
                                </div>
                              ))}
                              {court.conflicts.length > 5 && (
                                <p className={`text-xs ${theme.textSubtle}`}>...and {court.conflicts.length - 5} more</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                      <p className="text-amber-400 font-medium">
                        {conflictResults.courts.filter(c => !c.has_conflicts).length} of {conflictResults.courts.length} courts can be blocked
                      </p>
                      {conflictResults.courts.some(c => c.has_conflicts) && (
                        <p className={`text-sm ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                          Courts with conflicts will be skipped
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setAdvancedReserveStep(1)}>
                        Back
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => setAdvancedReserveStep(3)}
                        disabled={conflictResults.courts.every(c => c.has_conflicts)}
                      >
                        Next: Add Note
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 3: Note */}
                {advancedReserveStep === 3 && (
                  <>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <p className="text-emerald-400 font-medium mb-1">Ready to block:</p>
                      <div className="space-y-1">
                        {conflictResults?.courts.filter(c => !c.has_conflicts).map(court => (
                          <div key={court.court_id} className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                            ✓ {court.court_name}
                          </div>
                        ))}
                      </div>
                      <p className={`text-sm mt-2 ${theme.textMuted}`}>
                        {conflictResults?.dates.length} day{conflictResults?.dates.length > 1 ? 's' : ''} • {conflictResults?.time_range}
                      </p>
                    </div>

                    <div>
                      <Label className={theme.text}>
                        Note <span className="text-rose-400">*</span>
                      </Label>
                      <textarea
                        value={advancedReserveForm.note}
                        onChange={(e) => setAdvancedReserveForm(p => ({ ...p, note: e.target.value }))}
                        className={`w-full h-24 px-3 py-2 rounded-md mt-1 resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} border`}
                        placeholder="Enter reason for blocking these slots (required)..."
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setAdvancedReserveStep(2)}>
                        Back
                      </Button>
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={processAdvancedReserve}
                        disabled={!advancedReserveForm.note.trim()}
                      >
                        <ShieldBan className="h-4 w-4 mr-2" />
                        Create Block-offs
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Special Package Modal */}
        {showSpecialPackageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-4xl my-8 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <Package className="h-5 w-5 text-orange-500" />
                  Special Package Booking
                </CardTitle>
                <Button size="icon" variant="ghost" onClick={() => { setShowSpecialPackageModal(false); resetSpecialPackageForm(); }} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[75vh] overflow-y-auto">
                {/* Customer Information */}
                <div className="space-y-3">
                  <h4 className={`font-medium ${theme.text}`}>Customer Information</h4>
                  <div className="grid gap-3">
                    <div>
                      <Label className={theme.text}>Name <span className="text-rose-400">*</span></Label>
                      <Input
                        value={specialPackageForm.customer_name}
                        onChange={(e) => setSpecialPackageForm(p => ({ ...p, customer_name: e.target.value }))}
                        placeholder="Customer name"
                        className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                        data-testid="sp-customer-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Email</Label>
                        <Input
                          type="email"
                          value={specialPackageForm.customer_email}
                          onChange={(e) => setSpecialPackageForm(p => ({ ...p, customer_email: e.target.value }))}
                          placeholder="Email address"
                          className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                        />
                      </div>
                      <div>
                        <Label className={theme.text}>Phone</Label>
                        <Input
                          value={specialPackageForm.customer_phone}
                          onChange={(e) => setSpecialPackageForm(p => ({ ...p, customer_phone: e.target.value }))}
                          placeholder="Phone number"
                          className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                        />
                      </div>
                    </div>
                    {/* Check-in and Check-out Dates */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={theme.text}>Check-in Date <span className="text-rose-400">*</span></Label>
                        <Input
                          type="date"
                          value={specialPackageForm.check_in_date}
                          onChange={(e) => {
                            setSpecialPackageForm(p => ({ 
                              ...p, 
                              check_in_date: e.target.value,
                              day_bookings: [] // Reset day bookings when dates change
                            }));
                            setTempDayBooking({ date: '', court_id: '', time_slots: [] });
                            setModalAvailability([]);
                          }}
                          min={getLocalDateString()}
                          className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                          data-testid="sp-check-in"
                        />
                      </div>
                      <div>
                        <Label className={theme.text}>Check-out Date <span className="text-rose-400">*</span></Label>
                        <Input
                          type="date"
                          value={specialPackageForm.check_out_date}
                          onChange={(e) => {
                            setSpecialPackageForm(p => ({ 
                              ...p, 
                              check_out_date: e.target.value,
                              day_bookings: [] // Reset day bookings when dates change
                            }));
                            setTempDayBooking({ date: '', court_id: '', time_slots: [] });
                            setModalAvailability([]);
                          }}
                          min={specialPackageForm.check_in_date || getLocalDateString()}
                          className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                          data-testid="sp-check-out"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Selection */}
                <div className="space-y-3">
                  <h4 className={`font-medium ${theme.text}`}>Select Package</h4>
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {specialPackages.filter(p => p.is_active).map(pkg => (
                      <button
                        key={pkg.package_id}
                        onClick={() => {
                          setSpecialPackageForm(p => ({ 
                            ...p, 
                            package_id: pkg.package_id,
                            day_bookings: [] // Reset day bookings when package changes
                          }));
                          setTempDayBooking({ date: '', court_id: '', time_slots: [] });
                          setModalAvailability([]);
                        }}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          specialPackageForm.package_id === pkg.package_id
                            ? 'border-orange-500 bg-orange-500/20'
                            : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className={`font-medium ${specialPackageForm.package_id === pkg.package_id ? 'text-orange-400' : theme.text}`}>
                              {pkg.name}
                            </p>
                            <p className={`text-sm ${theme.textMuted}`}>{pkg.description}</p>
                            <p className={`text-xs ${theme.textSubtle} mt-1`}>
                              {pkg.days} day{pkg.days > 1 ? 's' : ''} • {pkg.hours_per_day} hour{pkg.hours_per_day > 1 ? 's' : ''}/day
                            </p>
                          </div>
                          <span className={`font-bold ${specialPackageForm.package_id === pkg.package_id ? 'text-orange-400' : 'text-emerald-500'}`}>
                            ₱{pkg.price.toLocaleString()}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Multi-Day Booking Section */}
                {specialPackageForm.package_id && specialPackageForm.check_in_date && specialPackageForm.check_out_date && (
                  <div className="space-y-4">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'}`}>
                      <p className="text-orange-500 font-medium text-sm">Booking Requirements</p>
                      <p className={`text-xs ${theme.textMuted} mt-1`}>
                        Package: {getSelectedPackage()?.days} day(s) • Available dates: {getDateRange().length} day(s) • 
                        Maximum bookable: {getMaxBookableDays()} day(s) • 
                        Hours per day: {getSelectedPackage()?.hours_per_day}
                      </p>
                      <p className={`text-xs ${theme.textSubtle} mt-1`}>
                        You can book up to {getMaxBookableDays()} day(s) or fewer.
                      </p>
                    </div>

                    {/* Selected Day Bookings List */}
                    {specialPackageForm.day_bookings.length > 0 && (
                      <div className="space-y-2">
                        <h4 className={`font-medium ${theme.text}`}>Booked Days ({specialPackageForm.day_bookings.length})</h4>
                        <div className="space-y-2">
                          {specialPackageForm.day_bookings.map((dayBooking, idx) => {
                            const court = courts.find(c => c.court_id === dayBooking.court_id);
                            return (
                              <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                                <div>
                                  <p className={`font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                                    Day {idx + 1}: {dayBooking.date}
                                  </p>
                                  <p className={`text-xs ${theme.textMuted}`}>
                                    {court?.name || dayBooking.court_id} • {dayBooking.time_slots.map(t => {
                                      const hour = parseInt(t.split(':')[0]);
                                      return hour > 12 ? `${hour - 12}PM` : hour === 12 ? '12PM' : `${hour}AM`;
                                    }).join(', ')}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-rose-400 hover:text-rose-300"
                                  onClick={() => removeDayBooking(idx)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Add New Day Booking Form */}
                    {specialPackageForm.day_bookings.length < getMaxBookableDays() && (
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                        <h4 className={`font-medium mb-3 ${theme.text}`}>Add Day {specialPackageForm.day_bookings.length + 1}</h4>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <Label className={theme.text}>Booking Date <span className="text-rose-400">*</span></Label>
                            <select
                              value={tempDayBooking.date}
                              onChange={(e) => {
                                const newDate = e.target.value;
                                setTempDayBooking(prev => ({ ...prev, date: newDate, time_slots: [] }));
                                if (newDate && tempDayBooking.court_id) {
                                  fetchModalAvailability(newDate, tempDayBooking.court_id);
                                } else {
                                  setModalAvailability([]);
                                }
                              }}
                              className={`mt-1 w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            >
                              <option value="">Select date</option>
                              {getDateRange()
                                .filter(d => !specialPackageForm.day_bookings.some(db => db.date === d))
                                .map(date => (
                                  <option key={date} value={date}>{date}</option>
                                ))}
                            </select>
                          </div>
                          <div>
                            <Label className={theme.text}>Court <span className="text-rose-400">*</span></Label>
                            <select
                              value={tempDayBooking.court_id}
                              onChange={(e) => {
                                const newCourtId = e.target.value;
                                setTempDayBooking(prev => ({ ...prev, court_id: newCourtId, time_slots: [] }));
                                if (newCourtId && tempDayBooking.date) {
                                  fetchModalAvailability(tempDayBooking.date, newCourtId);
                                } else {
                                  setModalAvailability([]);
                                }
                              }}
                              className={`mt-1 w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            >
                              <option value="">Select court</option>
                              {courts.filter(c => c.status === 'active' || !c.status).map(court => (
                                <option key={court.court_id} value={court.court_id}>{court.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Time Slot Selection */}
                        {tempDayBooking.date && tempDayBooking.court_id && modalAvailability.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className={theme.text}>Select Time Slots</Label>
                              <span className={`text-sm ${
                                tempDayBooking.time_slots.length === getSelectedPackage()?.hours_per_day
                                  ? 'text-emerald-400' : 'text-amber-400'
                              }`}>
                                {tempDayBooking.time_slots.length} / {getSelectedPackage()?.hours_per_day} hours
                              </span>
                            </div>
                            <div className="grid grid-cols-6 gap-2">
                              {modalAvailability.map(slot => {
                                const isSelected = tempDayBooking.time_slots.includes(slot.time);
                                const hour = parseInt(slot.time.split(':')[0]);
                                const displayTime = hour > 12 ? `${hour - 12}PM` : hour === 12 ? '12PM' : `${hour}AM`;
                                
                                return (
                                  <button
                                    key={slot.time}
                                    onClick={() => slot.available && toggleTempDaySlot(slot.time)}
                                    disabled={!slot.available}
                                    className={`p-2 rounded-lg border text-center text-sm transition-colors ${
                                      slot.isPast
                                        ? 'bg-slate-800/60 border-slate-700 text-slate-600 cursor-not-allowed'
                                        : slot.isBooked
                                          ? 'bg-rose-900/30 border-rose-800/50 text-rose-400/70 cursor-not-allowed'
                                          : isSelected
                                            ? 'border-orange-500 bg-orange-500/30 text-orange-400'
                                            : isDarkMode 
                                              ? 'border-slate-600 hover:border-orange-400 text-slate-300 hover:bg-orange-500/10' 
                                              : 'border-gray-200 hover:border-orange-400 text-gray-700 hover:bg-orange-50'
                                    }`}
                                  >
                                    {displayTime}
                                    {slot.isPast && <span className="block text-xs text-slate-600">Past</span>}
                                    {slot.isBooked && !slot.isPast && <span className="block text-xs">Booked</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Add Day Button */}
                        <Button
                          onClick={addDayBooking}
                          disabled={
                            !tempDayBooking.date || 
                            !tempDayBooking.court_id || 
                            tempDayBooking.time_slots.length !== getSelectedPackage()?.hours_per_day
                          }
                          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Day {specialPackageForm.day_bookings.length + 1}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Add-ons */}
                {/* <div className="space-y-3">
                  <h4 className={`font-medium ${theme.text}`}>Add-ons (Optional)</h4>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {addOns.filter(a => a.is_active !== false).map(addon => (
                      <div 
                        key={addon.item_id}
                        className={`p-2 rounded-lg border ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className={`text-sm font-medium ${theme.text}`}>{addon.name}</p>
                            <p className={`text-xs ${theme.textMuted}`}>₱{addon.price}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => setSpecialPackageForm(p => ({
                                ...p,
                                selected_addons: {
                                  ...p.selected_addons,
                                  [addon.item_id]: Math.max(0, (p.selected_addons[addon.item_id] || 0) - 1)
                                }
                              }))}
                              disabled={!specialPackageForm.selected_addons[addon.item_id]}
                            >
                              -
                            </Button>
                            <span className={`w-6 text-center text-sm ${theme.text}`}>
                              {specialPackageForm.selected_addons[addon.item_id] || 0}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => setSpecialPackageForm(p => ({
                                ...p,
                                selected_addons: {
                                  ...p.selected_addons,
                                  [addon.item_id]: (p.selected_addons[addon.item_id] || 0) + 1
                                }
                              }))}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div> */}

                {/* Transaction Type */}
                <div className="space-y-3">
                  <h4 className={`font-medium ${theme.text}`}>Transaction Type</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSpecialPackageForm(p => ({ ...p, transaction_type: 'reservation' }))}
                      className={`flex-1 p-3 rounded-lg border text-center ${
                        specialPackageForm.transaction_type === 'reservation'
                          ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                          : isDarkMode ? 'border-slate-600' : 'border-gray-200'
                      }`}
                    >
                      <p className="font-medium">Reservation</p>
                      <p className={`text-xs ${theme.textMuted}`}>Pay later</p>
                    </button>
                    <button
                      onClick={() => setSpecialPackageForm(p => ({ ...p, transaction_type: 'with_payment' }))}
                      className={`flex-1 p-3 rounded-lg border text-center ${
                        specialPackageForm.transaction_type === 'with_payment'
                          ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                          : isDarkMode ? 'border-slate-600' : 'border-gray-200'
                      }`}
                    >
                      <p className="font-medium">With Payment</p>
                      <p className={`text-xs ${theme.textMuted}`}>Paid now</p>
                    </button>
                  </div>
                </div>

                {/* Payment Method - Only show if With Payment selected */}
                {specialPackageForm.transaction_type === 'with_payment' && (
                  <div className="space-y-3">
                    <h4 className={`font-medium ${theme.text}`}>Payment Method</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { id: 'cash', label: 'Cash', icon: Banknote },
                        { id: 'card', label: 'Card', icon: CreditCard },
                        { id: 'e_wallet', label: 'E-Wallet', icon: Smartphone },
                        { id: 'online_banking', label: 'Online', icon: Building }
                      ].map(method => (
                        <button
                          key={method.id}
                          onClick={() => setSpecialPackageForm(p => ({ ...p, payment_method: method.id }))}
                          className={`p-2 rounded-lg border text-center ${
                            specialPackageForm.payment_method === method.id
                              ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                              : isDarkMode ? 'border-slate-600' : 'border-gray-200'
                          }`}
                        >
                          <method.icon className={`h-5 w-5 mx-auto mb-1 ${specialPackageForm.payment_method === method.id ? 'text-orange-400' : theme.textMuted}`} />
                          <p className={`text-xs ${specialPackageForm.payment_method === method.id ? 'text-orange-400' : theme.text}`}>{method.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${theme.text}`}>Total Amount</span>
                    <span className="text-xl font-bold text-orange-500">
                      ₱{calculateSpecialPackageTotal().toLocaleString()}
                    </span>
                  </div>
                  {getSelectedPackage() && (
                    <p className={`text-xs ${theme.textMuted} mt-1`}>
                      Package: ₱{getSelectedPackage().price.toLocaleString()}
                      {Object.values(specialPackageForm.selected_addons).some(v => v > 0) && ' + Add-ons'}
                      {specialPackageForm.day_bookings.length > 0 && ` • ${specialPackageForm.day_bookings.length} day(s) booked`}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 sticky bottom-0 bg-inherit pb-2">
                  <Button 
                    variant="outline" 
                    className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                    onClick={() => { setShowSpecialPackageModal(false); resetSpecialPackageForm(); }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={handleSpecialPackageSubmit}
                    disabled={
                      !specialPackageForm.customer_name.trim() || 
                      !specialPackageForm.package_id ||
                      !specialPackageForm.check_in_date ||
                      !specialPackageForm.check_out_date ||
                      specialPackageForm.day_bookings.length === 0
                    }
                    data-testid="sp-create-booking"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Create Booking ({specialPackageForm.day_bookings.length} day{specialPackageForm.day_bookings.length !== 1 ? 's' : ''})
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Booking Confirmation Modal (from redirect) */}
        {showDeleteConfirmModal && bookingToDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <Card className={`w-full max-w-lg mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
              <CardHeader className="pb-4">
                <CardTitle className={`flex items-center gap-2 text-rose-500`}>
                  <Trash2 className="h-5 w-5" />
                  Delete {bookingToDelete?.is_block_off || bookingToDelete?.source === 'block_off' || bookingToDelete?.booking_id?.startsWith('block_') ? 'Admin Reservation' : 'Booking'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-rose-900/20 border-rose-500/30' : 'bg-rose-50 border-rose-200'}`}>
                  <p className={`${isDarkMode ? 'text-rose-200' : 'text-rose-800'} font-medium mb-2`}>
                    Are you sure you want to delete this {bookingToDelete?.is_block_off || bookingToDelete?.source === 'block_off' || bookingToDelete?.booking_id?.startsWith('block_') ? 'admin reservation' : 'booking'}?
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-rose-300/80' : 'text-rose-700'}`}>
                    This action cannot be undone. The {bookingToDelete?.is_block_off || bookingToDelete?.source === 'block_off' || bookingToDelete?.booking_id?.startsWith('block_') ? 'reservation' : 'booking'} will be removed from the system.
                  </p>
                </div>
                
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-3 ${theme.text}`}>Booking Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className={theme.textMuted}>Booking ID:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.booking_id}</p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Customer:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.customer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Date:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.date}</p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Time:</span>
                      <p className={`font-medium ${theme.text}`}>
                        {formatKioskTimeDisplay(bookingToDelete.start_time)} - {formatKioskTimeDisplay(bookingToDelete.end_time)}
                      </p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Court:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.court_id}</p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Amount:</span>
                      <p className={`font-medium text-orange-500`}>
                        ₱{formatCurrency(bookingToDelete.total_amount || 0)}
                      </p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Status:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.status}</p>
                    </div>
                    <div>
                      <span className={theme.textMuted}>Payment:</span>
                      <p className={`font-medium ${theme.text}`}>{bookingToDelete.payment_status}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={closeDeleteConfirmModal}
                    disabled={isDeleting}
                    className={`${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDeleteBooking}
                    disabled={isDeleting}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    {isDeleting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Confirm Delete
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kiosk Transaction Details Modal */}
        {showKioskModal && selectedKioskTransaction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={theme.text}>Kiosk Booking Details</CardTitle>
                <Button size="icon" variant="ghost" onClick={closeKioskModal} className={theme.textMuted}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {kioskModalLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  </div>
                ) : (
                  <>
                    {/* Booking Source Badge */}
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                      isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
                    }`}>
                      <Package className="h-3 w-3" />
                      Source: Kiosk Booking
                    </div>

                    {/* Booking Info */}
                    <div className="space-y-3">
                      <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                        <FileText className="h-4 w-4" /> Booking Information
                      </h3>
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Transaction Code</span>
                          <span className={`${theme.text} font-mono text-sm`}>{selectedKioskTransaction.transaction_code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Customer</span>
                          <span className={theme.text}>{selectedKioskTransaction.name || 'Guest'}</span>
                        </div>
                        {selectedKioskTransaction.contact_number && (
                          <div className="flex justify-between">
                            <span className={theme.textMuted}>Contact</span>
                            <span className={theme.text}>{selectedKioskTransaction.contact_number}</span>
                          </div>
                        )}
                        {/* Show first court or multiple courts indicator */}
                        {(() => {
                          const bookingInfos = selectedKioskTransaction.booking_infos || [];
                          const courtList = [...new Set(bookingInfos.map(b => b.court_no || b.court_id))].sort((a, b) => a - b);
                          return (
                            <div className="flex justify-between">
                              <span className={theme.textMuted}>Court</span>
                              <span className={theme.text}>
                                {courtList.length === 1 ? `Court ${courtList[0]}` : `${courtList.length} Courts (${courtList.join(', ')})`}
                              </span>
                            </div>
                          );
                        })()}
                        {/* Show date & time for first booking or summary */}
                        {(() => {
                          const bookingInfos = selectedKioskTransaction.booking_infos || [];
                          if (bookingInfos.length === 1) {
                            const booking = bookingInfos[0];
                            return (
                              <div className="flex justify-between">
                                <span className={theme.textMuted}>Date & Time</span>
                                <span className={theme.text}>
                                  {booking.booking_date} • {
                                    booking.booking_time_start && booking.booking_time_end 
                                      ? `${formatKioskTimeDisplay(booking.booking_time_start)} - ${formatKioskTimeDisplay(booking.booking_time_end)}`
                                      : (booking.time_slots || []).map(t => formatKioskTimeDisplay(t)).join(', ')
                                  }
                                </span>
                              </div>
                            );
                          } else if (bookingInfos.length > 1) {
                            return (
                              <div className="flex justify-between">
                                <span className={theme.textMuted}>Date & Time</span>
                                <span className={theme.text}>{bookingInfos.length} bookings (see below)</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Status</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            selectedKioskTransaction.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedKioskTransaction.payment_status === 'reserved' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {selectedKioskTransaction.payment_status === 'paid' ? 'Confirmed' : selectedKioskTransaction.payment_status || 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Multiple Bookings Details (if more than 1 court/time) */}
                    {selectedKioskTransaction.booking_infos && selectedKioskTransaction.booking_infos.length > 1 && (
                      <div className="space-y-3">
                        <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                          <CalendarDays className="h-4 w-4" /> All Bookings
                        </h3>
                        <div className="space-y-2">
                          {selectedKioskTransaction.booking_infos.map((booking, idx) => (
                            <div key={idx} className={`${isDarkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3`}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className={`${theme.text} font-medium text-sm`}>Court {booking.court_no || booking.court_id}</p>
                                  <p className={`${theme.textMuted} text-xs mt-0.5`}>{booking.booking_date}</p>
                                </div>
                                <p className={`${theme.text} text-sm`}>
                                  {booking.booking_time_start && booking.booking_time_end 
                                    ? `${formatKioskTimeDisplay(booking.booking_time_start)} - ${formatKioskTimeDisplay(booking.booking_time_end)}`
                                    : (booking.time_slots || []).map(t => formatKioskTimeDisplay(t)).join(', ')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="space-y-3">
                      <h3 className={`text-sm font-medium ${theme.textMuted} flex items-center gap-2`}>
                        <CreditCard className="h-4 w-4" /> Payment Information
                      </h3>
                      <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-2`}>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Court Rental</span>
                          <span className={theme.text}>₱{formatCurrency(selectedKioskTransaction.total_amount || 0)}</span>
                        </div>
                        <div className={`flex justify-between pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}>
                          <span className={`${theme.textMuted} font-medium`}>Total Amount</span>
                          <span className="text-emerald-500 font-semibold text-lg">
                            ₱{formatCurrency(selectedKioskTransaction.total_amount || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Payment Method</span>
                          <span className={theme.text}>
                            {selectedKioskTransaction.payment_type === 'online' ? 'Credit/Debit Card' : 
                             selectedKioskTransaction.payment_type === 'counter' ? 'Counter Payment' :
                             selectedKioskTransaction.payment_type || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.textMuted}>Payment Status</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            selectedKioskTransaction.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            selectedKioskTransaction.payment_status === 'reserved' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {selectedKioskTransaction.payment_status || 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons for Reserved Transactions */}
                    {selectedKioskTransaction.payment_status === 'reserved' && (
                      <div className="space-y-3">
                        {/* Pay Now Section */}
                        {!showKioskPaymentForm ? (
                          <Button 
                            size="sm" 
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => {
                              setSelectedKioskPaymentMethod('cash');
                              setShowKioskPaymentForm(true);
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay Now
                          </Button>
                        ) : (
                          <div className={`${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3 space-y-3`}>
                            <Label className={`${theme.text} text-sm font-medium`}>Select Payment Method</Label>
                            <div className="grid grid-cols-4 gap-2">
                              {PAYMENT_METHODS.map((method) => {
                                const Icon = method.icon;
                                return (
                                  <button
                                    key={method.id}
                                    onClick={() => setSelectedKioskPaymentMethod(method.id)}
                                    className={`p-2 rounded-lg border text-center transition-colors ${
                                      selectedKioskPaymentMethod === method.id
                                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-500'
                                        : isDarkMode ? 'border-slate-600 text-slate-400 hover:border-slate-500' : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                    }`}
                                  >
                                    <Icon className="h-4 w-4 mx-auto mb-1" />
                                    <span className="text-xs">{method.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                                onClick={() => setShowKioskPaymentForm(false)}
                              >
                                Cancel
                              </Button>
                              <Button 
                                size="sm" 
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={kioskPaymentLoading}
                                onClick={async () => {
                                  const success = await confirmKioskPayment(
                                    selectedKioskTransaction.transaction_code,
                                    selectedKioskPaymentMethod
                                  );
                                  if (success) {
                                    setShowKioskPaymentForm(false);
                                    setSelectedKioskTransaction(prev => ({
                                      ...prev,
                                      payment_status: 'paid',
                                      payment_type: selectedKioskPaymentMethod
                                    }));
                                    fetchData(true);
                                  }
                                }}
                              >
                                {kioskPaymentLoading ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                  </div>
                                ) : (
                                  'Confirm Payment'
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBookingsPage;
